import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  Download,
  Plus,
  Trash2,
  Edit,
  Eye,
  FileText,
  IndianRupee,
  Package,
  User,
  Building,
  Truck,
  Calendar,
  AlertCircle,
  CheckCircle,
  X,
  ArrowLeft,
  History,
  Search,
  Mail,
  Phone,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import {
  handleTextInputChange,
  handleNumberInputChange,
  handlePhoneInputChange,
  getCharacterCount,
  validateRequiredFields,
  validatePhoneInput,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatCurrency, formatDate } from '../../../utils/stringUtils';
import { getQuotationStatusClasses, formatQuotationStatus, QUOTATION_STATUS_COLORS } from '../../../utils/quotationStatusUtils';
import { post, del } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';
import opportunityService from '../../../api/crm/opportunities/opportunityService';
import quotationService from '../../../api/crm/quotations/quotationService';
import productService from '../../../api/crm/products/productService';
import customerService from '../../../api/crm/customers/customerService';
import leadService from '../../../api/crm/leads/leadService';

const QuoteBuilder = () => {
  const navigate = useNavigate();
  const { opportunityId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('products');
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [quoteStatus, setQuoteStatus] = useState('draft');
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Address Options State
  const [billingCountryOptions, setBillingCountryOptions] = useState([]);
  const [billingStateOptions, setBillingStateOptions] = useState([]);
  const [billingCityOptions, setBillingCityOptions] = useState([]);

  const [shippingCountryOptions, setShippingCountryOptions] = useState([]);
  const [shippingStateOptions, setShippingStateOptions] = useState([]);
  const [shippingCityOptions, setShippingCityOptions] = useState([]);

  // Form state for add product modal
  const [newProductForm, setNewProductForm] = useState({
    productId: '',
    productName: '',
    composition: '',
    sku: '',
    quantity: 1,
    mrp: 0,
    unitPrice: 0,
    unitPricePTS: 0,
    batch: '',
    expiry: '',
    manufacturing: '',
    discount: 0,
    // Do not default tax; user must enter it explicitly
    tax: 0,
    stock: 0, // Store stock for quantity validation
    cost: 0, // Actual Rate
    purchaseRate: 0, // Purchase Rate
    rateA: 0,
    rateB: 0,
    rateC: 0
  });
  const [productFormErrors, setProductFormErrors] = useState({});



  // Get opportunity ID, customer ID, or quotation ID from query parameters or URL params
  const searchParams = new URLSearchParams(location.search);
  const opportunityIdFromQuery = searchParams.get('opportunityId');
  const customerIdFromQuery = searchParams.get('customerId');
  const quotationIdFromQuery = searchParams.get('quotationId');
  const finalOpportunityId = opportunityId || opportunityIdFromQuery;

  // State for opportunity, customer, and contact data
  const [opportunity, setOpportunity] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [contact, setContact] = useState(null);
  const [errorOpportunity, setErrorOpportunity] = useState(null);

  // React Query: Load quotation data if quotationId is provided (for editing)
  const {
    data: quotationData,
    isLoading: isLoadingQuotation,
    isError: isErrorQuotation,
    error: quotationError
  } = useQuery({
    queryKey: ['quotation', quotationIdFromQuery],
    queryFn: async () => {
      if (!quotationIdFromQuery) return null;
      const result = await quotationService.getQuotationById(quotationIdFromQuery);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load quotation data.');
      }
      return result.quotation;
    },
    enabled: !!quotationIdFromQuery,
    retry: 1,
  });

  // React Query: Load opportunity data from quotation (if needed)
  const quotationOppId = quotationData?.opportunityId?._id || quotationData?.opportunityId?.id || quotationData?.opportunityId;
  const shouldLoadQuotationOpp = quotationOppId && typeof quotationData?.opportunityId !== 'object' && !quotationData?.opportunityId?.name;

  const {
    data: quotationOpportunityData,
    isLoading: isLoadingQuotationOpp
  } = useQuery({
    queryKey: ['opportunity', quotationOppId],
    queryFn: async () => {
      const result = await opportunityService.getOpportunityById(quotationOppId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load opportunity data.');
      }
      return result.opportunity;
    },
    enabled: !!shouldLoadQuotationOpp,
    retry: 1,
  });

  // React Query: Load opportunity data for new quotation
  const {
    data: opportunityData,
    isLoading: isLoadingOpportunity,
    isError: isErrorOpportunity,
    error: opportunityError,
    refetch: refetchOpportunity
  } = useQuery({
    queryKey: ['opportunity', finalOpportunityId],
    queryFn: async () => {
      if (!finalOpportunityId) return null;
      const result = await opportunityService.getOpportunityById(finalOpportunityId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load opportunity data.');
      }
      return result.opportunity;
    },
    enabled: !!finalOpportunityId && !quotationIdFromQuery && !customerIdFromQuery,
    retry: 1,
  });

  // React Query: Load lead data when opportunity is loaded
  const leadIdForQuery = opportunityData?.leadId?._id || (typeof opportunityData?.leadId === 'string' ? opportunityData.leadId : null);
  const {
    data: leadData,
    isLoading: isLoadingLead,
    isError: isErrorLead,
    error: leadLoadError
  } = useQuery({
    queryKey: ['lead', leadIdForQuery],
    queryFn: async () => {
      if (!leadIdForQuery) return null;
      const result = await leadService.getLeadById(leadIdForQuery);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load lead data.');
      }
      return result.lead;
    },
    enabled: !!leadIdForQuery && !quotationIdFromQuery && !customerIdFromQuery,
    retry: 1,
  });

  // React Query: Load customer data for new quotation
  const {
    data: customerData,
    isLoading: isLoadingCustomer,
    isError: isErrorCustomer,
    error: customerError,
    refetch: refetchCustomer
  } = useQuery({
    queryKey: ['customer', customerIdFromQuery],
    queryFn: async () => {
      if (!customerIdFromQuery) return null;
      const result = await customerService.getCustomerById(customerIdFromQuery);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load customer data.');
      }
      return result.customer;
    },
    enabled: !!customerIdFromQuery && !quotationIdFromQuery,
    retry: 1,
  });

  // Process quotation data when loaded
  useEffect(() => {
    if (quotationData) {
      // Set current quote ID for update
      setCurrentQuoteId(quotationData._id || quotationData.id);
      setQuoteStatus(quotationData.status || 'draft');

      // Set opportunity from quotation or from separate query
      if (quotationData.opportunityId && typeof quotationData.opportunityId === 'object' && quotationData.opportunityId.name) {
        setOpportunity(quotationData.opportunityId);
      } else if (quotationOpportunityData) {
        setOpportunity(quotationOpportunityData);
      }

      // Set customer from quotation if available (when created from customer)
      if (quotationData.customerId && typeof quotationData.customerId === 'object' && quotationData.customerId.companyName) {
        // customerId is populated as an object
        setCustomer(quotationData.customerId);
      } else if (quotationData.customer && typeof quotationData.customer === 'object' && quotationData.customer.companyName) {
        // customer snapshot is available
        setCustomer(quotationData.customer);
      }

      // Load contact data from Lead (if from opportunity) or Customer (if from customer)
      if (quotationData.lead) {
        // From opportunity - use lead data
        const lead = quotationData.lead;
        setContact({
          firstName: lead.firstName,
          lastName: lead.lastName,
          email: lead.email,
          phone: lead.phone,
          designation: lead.designation || ''
        });
      } else if (quotationData.customerId && typeof quotationData.customerId === 'object' && quotationData.customerId.companyName) {
        // From customer - use customerId data
        const customerData = quotationData.customerId;
        const contactPersonParts = (customerData.contactPerson || '').split(' ');
        setContact({
          firstName: contactPersonParts[0] || '',
          lastName: contactPersonParts.slice(1).join(' ') || '',
          email: customerData.email || '',
          phone: (Array.isArray(customerData.phone) ? customerData.phone[0] : customerData.phone) || '',
          designation: ''
        });
      } else if (quotationData.customer && typeof quotationData.customer === 'object' && quotationData.customer.companyName) {
        // From customer - use customer snapshot
        const customerData = quotationData.customer;
        const contactPersonParts = (customerData.contactPerson || '').split(' ');
        setContact({
          firstName: contactPersonParts[0] || '',
          lastName: contactPersonParts.slice(1).join(' ') || '',
          email: customerData.email || '',
          phone: (Array.isArray(customerData.phone) ? customerData.phone[0] : customerData.phone) || '',
          designation: ''
        });
      }

      // Pre-fill quote details
      // Convert expiryDate to YYYY-MM-DD format for date input
      let expiryDateValue = '';
      if (quotationData.expiryDate) {
        const expiryDate = new Date(quotationData.expiryDate);
        if (!isNaN(expiryDate.getTime())) {
          expiryDateValue = expiryDate.toISOString().split('T')[0];
        }
      }
      setQuoteDetails({
        title: quotationData.title || '',
        expiryDate: expiryDateValue,
        email: quotationData.email || '',
        terms: quotationData.terms || '',
        notes: quotationData.notes || '',
        priority: quotationData.priority || 'normal'
      });

      // Pre-fill billing & shipping addresses
      if (quotationData.billingAddress || quotationData.shippingAddress) {
        setQuoteAddresses(prev => ({
          billingAddress: {
            company: quotationData.billingAddress?.company || prev.billingAddress.company,
            address: quotationData.billingAddress?.street || quotationData.billingAddress?.address || prev.billingAddress.address,
            city: (typeof quotationData.billingAddress?.city === 'object' ? quotationData.billingAddress.city : (quotationData.billingAddress?.city ? { name: quotationData.billingAddress.city } : null)),
            state: (typeof quotationData.billingAddress?.state === 'object' ? quotationData.billingAddress.state : (quotationData.billingAddress?.state ? { name: quotationData.billingAddress.state } : null)),
            country: (typeof quotationData.billingAddress?.country === 'object' ? quotationData.billingAddress.country : (quotationData.billingAddress?.country ? { name: quotationData.billingAddress.country } : { name: 'India', isoCode: 'IN' })),
            pincode: quotationData.billingAddress?.pincode || prev.billingAddress.pincode,
            gstin: quotationData.billingAddress?.gstin || prev.billingAddress.gstin,
            drugLicenseNumber: quotationData.billingAddress?.drugLicenseNumber || prev.billingAddress.drugLicenseNumber,
            drugLicenseExpiry: quotationData.billingAddress?.drugLicenseExpiry ? new Date(quotationData.billingAddress.drugLicenseExpiry).toISOString().split('T')[0] : (prev.billingAddress.drugLicenseExpiry || '')
          },
          shippingAddress: {
            company: quotationData.shippingAddress?.company || prev.shippingAddress.company,
            address: quotationData.shippingAddress?.street || quotationData.shippingAddress?.address || prev.shippingAddress.address,
            landmark: quotationData.shippingAddress?.landmark || prev.shippingAddress.landmark,
            city: (typeof quotationData.shippingAddress?.city === 'object' ? quotationData.shippingAddress.city : (quotationData.shippingAddress?.city ? { name: quotationData.shippingAddress.city } : null)),
            state: (typeof quotationData.shippingAddress?.state === 'object' ? quotationData.shippingAddress.state : (quotationData.shippingAddress?.state ? { name: quotationData.shippingAddress.state } : null)),
            country: (typeof quotationData.shippingAddress?.country === 'object' ? quotationData.shippingAddress.country : (quotationData.shippingAddress?.country ? { name: quotationData.shippingAddress.country } : { name: 'India', isoCode: 'IN' })),
            pincode: quotationData.shippingAddress?.pincode || prev.shippingAddress.pincode,
            contact: quotationData.shippingAddress?.contact || prev.shippingAddress.contact,
            phone: (Array.isArray(quotationData.shippingAddress?.phone) ? quotationData.shippingAddress.phone[0] : (quotationData.shippingAddress?.phone || prev.shippingAddress.phone)),
            deliveryInstructions: quotationData.shippingAddress?.deliveryInstructions || prev.shippingAddress.deliveryInstructions
          }
        }));
      }

      // Load quote items
      if (quotationData.items && Array.isArray(quotationData.items) && quotationData.items.length > 0) {
        const formattedItems = quotationData.items.map((item, index) => ({
          id: item._id || item.id || Date.now() + index,
          productId: item.productId || null,
          productName: item.productName || '',
          composition: item.composition || '',
          sku: item.sku || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          mrp: item.mrp || 0,
          unitPricePTS: item.unitPricePTS || 0,
          batch: item.batch || '',
          expiry: item.expiry || '',
          manufacturing: item.manufacturing || '',
          discount: item.discount || 0,
          tax: item.tax ?? 0,
          lineTotal: item.lineTotal || 0
        }));
        setQuoteItems(formattedItems);
      }

      // Load attachments
      if (quotationData.attachments && Array.isArray(quotationData.attachments) && quotationData.attachments.length > 0) {
        const formattedAttachments = quotationData.attachments.map((att, index) => ({
          id: att._id || att.id || Date.now() + index,
          name: att.filename || att.originalName || `attachment-${index}`,
          url: att.url,
          size: att.size || 0,
          type: att.mimeType || 'application/octet-stream',
          isExisting: true,
          public_id: att.public_id
        }));
        setUploadedFiles(formattedAttachments);
        setAttachments(formattedAttachments);
      }
    }
  }, [quotationData, quotationOpportunityData]);

  // Process opportunity and lead data when loaded (for new quotations)
  useEffect(() => {
    if (opportunityData && !quotationIdFromQuery && !customerIdFromQuery) {
      setOpportunity(opportunityData);
    }

    if (leadData && !quotationIdFromQuery && !customerIdFromQuery) {
      // Set lead information into contact state
      const lead = leadData;
      setContact({
        firstName: lead.firstName || '',
        lastName: lead.lastName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        designation: lead.designation || ''
      });

      // Auto-fill quotation details
      setQuoteDetails(prev => ({
        ...prev,
        email: prev.email || lead.email || '',
        title: prev.title || (lead.company ? `Quotation for ${lead.company}` : '')
      }));

      // NOTE: Address autofill is handled by a separate useEffect that watches leadData
    }
  }, [opportunityData, leadData, quotationIdFromQuery, customerIdFromQuery]);

  // Process customer data when loaded (for new quotations)
  useEffect(() => {
    if (customerData && !quotationIdFromQuery) {
      setCustomer(customerData);

      // Set contact data from customer
      setContact({
        firstName: customerData.contactPerson?.split(' ')[0] || '',
        lastName: customerData.contactPerson?.split(' ').slice(1).join(' ') || '',
        email: customerData.email || '',
        phone: (Array.isArray(customerData.phone) ? customerData.phone[0] : customerData.phone) || '',
        designation: ''
      });

      // Auto-fill addresses from customer
      const billingAddress = customerData.billingAddress || {};
      const shippingAddress = customerData.shippingAddress || {};

      setQuoteAddresses(prev => ({
        billingAddress: {
          company: customerData.companyName || prev.billingAddress.company,
          address: billingAddress.street || prev.billingAddress.address,
          city: (typeof billingAddress.city === 'object' ? billingAddress.city : (billingAddress.city ? { name: billingAddress.city } : null)),
          state: (typeof billingAddress.state === 'object' ? billingAddress.state : (billingAddress.state ? { name: billingAddress.state } : null)),
          country: (typeof billingAddress.country === 'object' ? billingAddress.country : (billingAddress.country ? { name: billingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })),
          pincode: billingAddress.pincode || prev.billingAddress.pincode,
          gstin: customerData.gstNumber || prev.billingAddress.gstin,
          drugLicenseNumber: customerData.drugLicenseNumber || prev.billingAddress.drugLicenseNumber,
          drugLicenseExpiry: customerData.drugLicenseExpiry ? new Date(customerData.drugLicenseExpiry).toISOString().split('T')[0] : (prev.billingAddress.drugLicenseExpiry || '')
        },
        shippingAddress: {
          company: customerData.companyName || prev.shippingAddress.company,
          address: shippingAddress.street || prev.shippingAddress.address,
          landmark: shippingAddress.landmark || prev.shippingAddress.landmark,
          city: (typeof shippingAddress.city === 'object' ? shippingAddress.city : (shippingAddress.city ? { name: shippingAddress.city } : null)),
          state: (typeof shippingAddress.state === 'object' ? shippingAddress.state : (shippingAddress.state ? { name: shippingAddress.state } : null)),
          country: (typeof shippingAddress.country === 'object' ? shippingAddress.country : (shippingAddress.country ? { name: shippingAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })),
          pincode: shippingAddress.pincode || prev.shippingAddress.pincode,
          contact: customerData.contactPerson || prev.shippingAddress.contact,
          phone: (Array.isArray(customerData.phone) ? customerData.phone[0] : customerData.phone) || prev.shippingAddress.phone,
          deliveryInstructions: shippingAddress.deliveryInstructions || prev.shippingAddress.deliveryInstructions
        }
      }));

      // Auto-fill quote title
      if (!quoteDetails.title) {
        const defaultTitle = customerData.companyName
          ? `Quotation for ${customerData.companyName}`
          : 'Quotation';

        setQuoteDetails(prev => ({
          ...prev,
          title: defaultTitle
        }));
      }


      // Auto-fill email and phone from customer
      setQuoteDetails(prev => ({
        ...prev,
        email: prev.email || customerData.email || ''
      }));
    }
  }, [customerData, quotationIdFromQuery]);

  // Handle errors
  useEffect(() => {
    if (isErrorQuotation) {
      setErrorOpportunity(quotationError?.message || 'Failed to load quotation data. Please try again.');
    } else if (isErrorOpportunity) {
      setErrorOpportunity(opportunityError?.message || 'Failed to load opportunity data. Please try again.');
    } else if (isErrorCustomer) {
      setErrorOpportunity(customerError?.message || 'Failed to load customer data. Please try again.');
    } else if (!finalOpportunityId && !quotationIdFromQuery && !customerIdFromQuery) {
      // No error - allow creating quote without opportunity or customer (user can fill manually)
      setErrorOpportunity(null);
    }
  }, [isErrorQuotation, isErrorOpportunity, isErrorCustomer, quotationError, opportunityError, customerError, finalOpportunityId, quotationIdFromQuery, customerIdFromQuery]);

  const loadingOpportunity = isLoadingQuotation || isLoadingQuotationOpp || isLoadingOpportunity || isLoadingCustomer;

  // Product catalog state
  const [productSearch, setProductSearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const productSearchInputRef = useRef(null);
  const wasInputFocusedRef = useRef(false);
  const isSettingProductFromSelectionRef = useRef(false);

  // React Query: Load products from API with search
  const {
    data: productsData,
    isLoading: loadingProducts,
    refetch: refetchProducts
  } = useQuery({
    queryKey: ['products', debouncedProductSearch],
    queryFn: async () => {
      const result = await productService.getProducts({
        search: debouncedProductSearch || undefined,
        limit: 20
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to load products');
      }

      const productsArray = Array.isArray(result.products) ? result.products : [];

      // Transform inventory items to match expected format
      return productsArray.map((item, index) => {
        const productName = (item.productName || '').trim();
        const productId = item.productId || item.id;
        const productCode = (item.productCode || item.sku || '').trim();
        const price = item.rateA || item.rateB || item.rateC || item.mrp || 0;

        return {
          id: item.id || `inventory-${index}`,
          productId: productId,
          pid: item.pid != null ? item.pid : productId,
          name: productName,
          productName: productName,
          composition: item.composition || '',
          sku: productCode,
          productCode: productCode,
          price: price,
          rateA: item.rateA || 0,
          rateB: item.rateB || 0,
          rateC: item.rateC || 0,
          mrp: item.mrp || 0,
          pts: item.cost || item.purchaseRate || 0,
          cost: item.cost || 0,
          purchaseRate: item.purchaseRate || 0,
          category: item.category || '',
          manufacturer: item.manufacturer || item.brand || '',
          stock: item.stock || 0,
          batch: item.batch ? item.batch.trim() : '',
          expiry: item.expiry || null,
          unit: item.unit ? item.unit.trim() : '',
          tax: item.tax || 0
        };
      });
    },
    enabled: Boolean(showAddProductModal && debouncedProductSearch?.trim()?.length > 0),
    staleTime: 0,
    retry: 1,
  });

  const productCatalog = productsData || [];

  const quoteCustomerId = customerIdFromQuery || customer?._id || customer?.id || null;

  const {
    data: productPurchaseHistory = [],
    isFetching: loadingProductPurchaseHistory
  } = useQuery({
    queryKey: [
      'productPurchaseHistory',
      quoteCustomerId,
      newProductForm.productName
    ],
    queryFn: async () => {
      const result = await customerService.getCustomerProductPurchaseHistory(quoteCustomerId, {
        productName: newProductForm.productName || undefined
      });
      if (!result.success) {
        return [];
      }
      return Array.isArray(result.items) ? result.items : [];
    },
    enabled: Boolean(
      showAddProductModal &&
      quoteCustomerId &&
      newProductForm.productName &&
      newProductForm.productName.trim().length > 0
    ),
    retry: 0
  });

  // React Query: Save draft mutation (create/update quotation)
  const saveDraftMutation = useMutation({
    mutationFn: async ({ quoteId, quoteData }) => {
      if (quoteId) {
        return await quotationService.updateQuotation(quoteId, quoteData);
      } else {
        return await quotationService.createQuotation(quoteData);
      }
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        if (!variables.quoteId && result.quotation) {
          setCurrentQuoteId(result.quotation._id || result.quotation.id);
          setQuoteStatus(result.quotation.status || 'draft');
        }
        // Show success message using toast
        toast.success('Quote saved as draft successfully!');

        // Invalidate quotation query if updating
        if (variables.quoteId) {
          queryClient.invalidateQueries({ queryKey: ['quotation', variables.quoteId] });
        }
      } else {
        toast.error(result.error || 'Failed to save quote');
      }
    },
    onError: (error) => {
      // Check for duplicate error specifically if needed, or just show message
      toast.error(error.message || 'Failed to save quote');
    },
  });

  // React Query: Create/Update quotation mutation
  const createUpdateQuotationMutation = useMutation({
    mutationFn: async ({ isUpdate, quoteId, formData }) => {
      if (isUpdate) {
        return await quotationService.updateQuotation(quoteId, formData);
      } else {
        return await quotationService.createQuotation(formData);
      }
    },
    onSuccess: (result, variables) => {
      if (!result.success) {
        throw new Error(result.error || (variables.isUpdate ? 'Failed to update quotation' : 'Failed to create quotation'));
      }
      toast.success(variables.isUpdate ? 'Quotation updated successfully' : 'Quotation created successfully');
      // Invalidate quotation query if updating
      if (variables.isUpdate && variables.quoteId) {
        queryClient.invalidateQueries({ queryKey: ['quotation', variables.quoteId] });
      }
      navigate('/crm/quotation-proposal/create-quotes');
    },
    onError: (error) => {
      toast.error(error.message || (error.isUpdate ? 'Failed to update quotation' : 'Failed to create quotation'));
    },
  });

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadError, setUploadError] = useState('');


  // Quote items (empty initially - user will add products)
  const [quoteItems, setQuoteItems] = useState([]);

  // Quote details (empty initially - will be auto-filled from opportunity)
  const [quoteDetails, setQuoteDetails] = useState({
    title: '',
    expiryDate: '',
    email: '',
    terms: '',
    notes: '',
    priority: 'normal'
  });

  // Billing & Shipping addresses for the quotation (optional but persisted)
  const [quoteAddresses, setQuoteAddresses] = useState({
    billingAddress: {
      company: '',
      address: '',
      city: null,
      state: null,
      country: { name: 'India', isoCode: 'IN' },
      pincode: '',
      gstin: '',
      drugLicenseNumber: '',
      drugLicenseExpiry: ''
    },
    shippingAddress: {
      company: '',
      address: '',
      landmark: '',
      city: null,
      state: null,
      country: { name: 'India', isoCode: 'IN' },
      pincode: '',
      contact: '',
      phone: '',
      deliveryInstructions: ''
    }
  });

  // Load countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries().map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setBillingCountryOptions(allCountries);
    setShippingCountryOptions(allCountries);
  }, []);

  // Sync Billing States
  useEffect(() => {
    if (quoteAddresses.billingAddress.country?.isoCode) {
      const states = State.getStatesOfCountry(quoteAddresses.billingAddress.country.isoCode).map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setBillingStateOptions(states);
    } else {
      setBillingStateOptions([]);
    }
  }, [quoteAddresses.billingAddress.country]);

  // Sync Billing Cities
  useEffect(() => {
    if (quoteAddresses.billingAddress.country?.isoCode && quoteAddresses.billingAddress.state?.isoCode) {
      const cities = City.getCitiesOfState(quoteAddresses.billingAddress.country.isoCode, quoteAddresses.billingAddress.state.isoCode).map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setBillingCityOptions(cities);
    } else {
      setBillingCityOptions([]);
    }
  }, [quoteAddresses.billingAddress.country, quoteAddresses.billingAddress.state]);

  // Sync Shipping States
  useEffect(() => {
    if (quoteAddresses.shippingAddress.country?.isoCode) {
      const states = State.getStatesOfCountry(quoteAddresses.shippingAddress.country.isoCode).map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setShippingStateOptions(states);
    } else {
      setShippingStateOptions([]);
    }
  }, [quoteAddresses.shippingAddress.country]);

  // Sync Shipping Cities
  useEffect(() => {
    if (quoteAddresses.shippingAddress.country?.isoCode && quoteAddresses.shippingAddress.state?.isoCode) {
      const cities = City.getCitiesOfState(quoteAddresses.shippingAddress.country.isoCode, quoteAddresses.shippingAddress.state.isoCode).map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setShippingCityOptions(cities);
    } else {
      setShippingCityOptions([]);
    }
  }, [quoteAddresses.shippingAddress.country, quoteAddresses.shippingAddress.state]);

  // Debounce product search
  useEffect(() => {
    // Skip debounce if we're setting search text from product selection
    if (isSettingProductFromSelectionRef.current) {
      isSettingProductFromSelectionRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedProductSearch(productSearch);
    }, 400);

    return () => clearTimeout(timer);
  }, [productSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProductDropdownOpen && !event.target.closest('.product-dropdown-container')) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isProductDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProductDropdownOpen]);

  // Maintain focus on search input during API calls
  useEffect(() => {
    if (showAddProductModal && productSearchInputRef.current && wasInputFocusedRef.current) {
      // Maintain focus when loading state changes (during API calls)
      const input = productSearchInputRef.current;
      // Use requestAnimationFrame to ensure focus happens after render
      requestAnimationFrame(() => {
        if (input && showAddProductModal && wasInputFocusedRef.current) {
          input.focus();
        }
      });
    }
  }, [loadingProducts, showAddProductModal]);

  // Focus search input when modal opens
  useEffect(() => {
    if (showAddProductModal && !loadingProducts) {
      const focusTimer = setTimeout(() => {
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
        }
      }, 200);
      return () => clearTimeout(focusTimer);
    }
  }, [showAddProductModal, loadingProducts]);

  // Set default expiry date (30 days from today) if not set and not editing
  useEffect(() => {
    if (!quotationIdFromQuery && !currentQuoteId && !quoteDetails.expiryDate) {
      const defaultExpiryDate = new Date();
      defaultExpiryDate.setDate(defaultExpiryDate.getDate() + 30);
      setQuoteDetails(prev => ({
        ...prev,
        expiryDate: defaultExpiryDate.toISOString().split('T')[0]
      }));
    }
  }, [quotationIdFromQuery, currentQuoteId]);

  // Auto-fill quote title from opportunity or customer name when data loads
  useEffect(() => {
    if ((opportunity || customer) && !quoteDetails.title) {
      const opportunityName = opportunity?.name || '';
      const companyName =
        customer?.companyName ||
        quoteAddresses.billingAddress.company ||
        opportunity?.leadId?.company ||
        '';
      const defaultTitle = companyName
        ? `Quotation for ${companyName}`
        : opportunityName
          ? `Quotation - ${opportunityName}`
          : 'Quotation';

      setQuoteDetails(prev => ({
        ...prev,
        title: defaultTitle
      }));
    }
  }, [opportunity, customer, quoteAddresses.billingAddress.company]);

  // Auto-fill company name in address fields if they are empty
  useEffect(() => {
    const compName = customer?.companyName || opportunity?.leadId?.company || '';
    if (compName) {
      setQuoteAddresses(prev => {
        let updated = false;
        const newAddresses = { ...prev };

        if (!newAddresses.billingAddress.company) {
          newAddresses.billingAddress = { ...newAddresses.billingAddress, company: compName };
          updated = true;
        }

        if (!newAddresses.shippingAddress.company) {
          newAddresses.shippingAddress = { ...newAddresses.shippingAddress, company: compName };
          updated = true;
        }

        return updated ? newAddresses : prev;
      });
    }
  }, [customer, opportunity]);

  // Validation helper functions
  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Sanitize product search input - remove restricted symbols
  // Blocked symbols: - < > / ' " ( ) = ` % & ; : # \ + - { } [ ] | @ $ * , . _ ~
  const sanitizeProductSearch = (input) => {
    if (!input) return '';
    // Remove restricted symbols using regex character class
    // Escape special regex characters: [ ] \ - and place - at end
    const restrictedSymbols = /[<>\/'"()=`%&;:#\\+{}[\]|@$*,._~-]/g;
    return input.replace(restrictedSymbols, '');
  };

  // Check if a value is a valid MongoDB ObjectId
  const isValidObjectId = (value) => {
    if (!value) return false;
    const str = String(value);
    // MongoDB ObjectId is 24 hex characters
    return /^[0-9a-fA-F]{24}$/.test(str);
  };

  // Sanitize productId - only send if it's a valid ObjectId, otherwise send null
  const sanitizeProductId = (productId) => {
    if (!productId) return null;
    return isValidObjectId(productId) ? productId : null;
  };

  // Format date as dd-mm-yyyy
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Shipping address validation handlers
  const handleShippingCompanyChange = (e) => {
    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.company);
    const capitalizedValue = capitalizeFirstLetter(sanitized);
    setQuoteAddresses(prev => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, company: capitalizedValue }
    }));
    if (errors.shippingCompany) {
      setErrors(prev => ({ ...prev, shippingCompany: '' }));
    }
  };

  const handleShippingContactChange = (e) => {
    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.customerName);
    const capitalizedValue = capitalizeFirstLetter(sanitized);
    setQuoteAddresses(prev => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, contact: capitalizedValue }
    }));
    if (errors.shippingContact) {
      setErrors(prev => ({ ...prev, shippingContact: '' }));
    }
  };

  const handleShippingCityChange = (e) => {
    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.city);
    setQuoteAddresses(prev => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, city: sanitized }
    }));
    if (errors.shippingCity) {
      setErrors(prev => ({ ...prev, shippingCity: '' }));
    }
  };

  const handleShippingStateChange = (e) => {
    const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.state);
    setQuoteAddresses(prev => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, state: sanitized }
    }));
    if (errors.shippingState) {
      setErrors(prev => ({ ...prev, shippingState: '' }));
    }
  };

  const handleShippingPincodeChange = (e) => {
    const validated = handleNumberInputChange(e, null, NUMBER_LIMITS.pincode.max, NUMBER_LIMITS.pincode.min);
    setQuoteAddresses(prev => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, pincode: validated.toString().slice(0, 6) }
    }));
    if (errors.shippingPincode) {
      setErrors(prev => ({ ...prev, shippingPincode: '' }));
    }
  };

  const handleShippingPhoneChange = (e) => {
    const phoneValue = handlePhoneInputChange(e, (value) => {
      setQuoteAddresses(prev => ({
        ...prev,
        shippingAddress: { ...prev.shippingAddress, phone: value }
      }));
    });
    const validation = validatePhoneInput(phoneValue);
    if (!validation.isValid && phoneValue) {
      setErrors(prev => ({ ...prev, shippingPhone: validation.error }));
    } else {
      setErrors(prev => ({ ...prev, shippingPhone: '' }));
    }
  };

  // Auto-fill addresses from fetched lead details (for new opportunity-based quotes)
  useEffect(() => {
    // Only for new quotations (not editing)
    if (currentQuoteId || quotationIdFromQuery) return;
    if (!leadData) return;

    const leadAddress = leadData.address || {};
    const companyName = leadData.company || '';

    // If lead does not have any address details or company name, skip
    if (!leadAddress.street && !leadAddress.city && !leadAddress.state && !leadAddress.pincode && !companyName) {
      return;
    }

    setQuoteAddresses(prev => ({
      billingAddress: {
        company: prev.billingAddress.company || companyName,
        address: prev.billingAddress.address || leadAddress.street || '',
        city: (typeof leadAddress.city === 'object' ? leadAddress.city : (leadAddress.city ? { name: leadAddress.city } : null)) || prev.billingAddress.city,
        state: (typeof leadAddress.state === 'object' ? leadAddress.state : (leadAddress.state ? { name: leadAddress.state } : null)) || prev.billingAddress.state,
        country: (typeof leadAddress.country === 'object' ? leadAddress.country : (leadAddress.country ? { name: leadAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })) || prev.billingAddress.country,
        pincode: leadAddress.pincode || prev.billingAddress.pincode || '',
        gstin: prev.billingAddress.gstin || leadData.gstNumber || '',
        drugLicenseNumber: prev.billingAddress.drugLicenseNumber || leadData.drugLicenseNumber || '',
        drugLicenseExpiry: (leadData.drugLicenseExpiry ? new Date(leadData.drugLicenseExpiry).toISOString().split('T')[0] : (prev.billingAddress.drugLicenseExpiry || ''))
      },
      shippingAddress: {
        company: prev.shippingAddress.company || companyName,
        address: prev.shippingAddress.address || leadAddress.street || '',
        landmark: prev.shippingAddress.landmark || leadAddress.landmark || '',
        city: (typeof leadAddress.city === 'object' ? leadAddress.city : (leadAddress.city ? { name: leadAddress.city } : null)) || prev.shippingAddress.city,
        state: (typeof leadAddress.state === 'object' ? leadAddress.state : (leadAddress.state ? { name: leadAddress.state } : null)) || prev.shippingAddress.state,
        country: (typeof leadAddress.country === 'object' ? leadAddress.country : (leadAddress.country ? { name: leadAddress.country, isoCode: 'IN' } : { name: 'India', isoCode: 'IN' })) || prev.shippingAddress.country,
        pincode: leadAddress.pincode || prev.shippingAddress.pincode || '',
        contact: prev.shippingAddress.contact || ((leadData.firstName || leadData.lastName) ? `${leadData.firstName || ''} ${leadData.lastName || ''}`.trim() : ''),
        phone: prev.shippingAddress.phone || leadData.phone || '',
        deliveryInstructions: prev.shippingAddress.deliveryInstructions || leadAddress.deliveryInstructions || ''
      }
    }));

    // New: Validate the auto-filled data immediately
    const newErrors = {};
    const leadPincode = leadAddress.pincode ? String(leadAddress.pincode) : '';

    // Check if we are using the lead pincode (i.e. existing field is empty) and if it is invalid
    if (!quoteAddresses.billingAddress.pincode && leadPincode && leadPincode.length < 6) {
      newErrors.billingPincode = 'Pincode must be at least 6 digits';
    }

    if (!quoteAddresses.shippingAddress.pincode && leadPincode && leadPincode.length < 6) {
      newErrors.shippingPincode = 'Pincode must be at least 6 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
    }
  }, [leadData, currentQuoteId, quotationIdFromQuery]);


  const calculateLineTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = (subtotal * item.discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * item.tax) / 100;
    const lineTotal = taxableAmount + taxAmount;
    // Round to 2 decimal places
    return Math.round(lineTotal * 100) / 100;
  };

  const calculateTotals = () => {
    const subtotal = quoteItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const totalDiscount = quoteItems.reduce((sum, item) => sum + ((item.quantity * item.unitPrice * item.discount) / 100), 0);
    const taxableAmount = subtotal - totalDiscount;
    const totalTax = quoteItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount = (itemSubtotal * item.discount) / 100;
      const itemTaxable = itemSubtotal - itemDiscount;
      return sum + ((itemTaxable * item.tax) / 100);
    }, 0);
    const grandTotal = taxableAmount + totalTax;

    // Round all values to 2 decimal places
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      taxableAmount: Math.round(taxableAmount * 100) / 100,
      totalTax: Math.round(totalTax * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100
    };
  };

  const getProductNameById = (productId) => {
    if (!productId) return '';
    const product = productCatalog.find(p => p.id === productId || p.productId === productId);
    return product ? product.name || product.productName : '';
  };

  const getProductCompositionById = (productId) => {
    if (!productId) return '';
    const product = productCatalog.find(p => p.id === productId || p.productId === productId);
    return product ? (product.composition || '') : '';
  };

  const handleEditItem = async (itemId) => {
    const itemToEdit = quoteItems.find(item => item.id === itemId);
    if (itemToEdit) {
      // Set search text to product name for display
      let product = productCatalog.find(p =>
        p.id === itemToEdit.productId ||
        p.productId === itemToEdit.productId ||
        p._id === itemToEdit.productId
      );

      // If product not found in catalog and we have productId or productName, try to fetch it
      if (!product && (itemToEdit.productId || itemToEdit.productName)) {
        try {
          // Search for the product to get latest stock
          const searchTerm = itemToEdit.productName || itemToEdit.sku || '';
          if (searchTerm) {
            const searchResult = await productService.getProducts({
              search: searchTerm,
              limit: 20
            });

            if (searchResult.success && searchResult.products) {
              // Find matching product by productId or productName
              product = searchResult.products.find(p => {
                if (itemToEdit.productId) {
                  return String(p.id) === String(itemToEdit.productId) ||
                    String(p.productId) === String(itemToEdit.productId) ||
                    String(p._id) === String(itemToEdit.productId);
                }
                // Match by product name if no productId
                const pName = (p.productName || p.name || '').toLowerCase().trim();
                const itemName = (itemToEdit.productName || '').toLowerCase().trim();
                return pName === itemName;
              });

              // Transform to match catalog format if found
              if (product) {
                product = {
                  id: product.id || product._id,
                  productId: product.productId || product.id,
                  name: product.productName || product.name,
                  productName: product.productName || product.name,
                  composition: product.composition || '',
                  sku: product.productCode || product.sku,
                  productCode: product.productCode || product.sku,
                  price: product.rateA || product.rateB || product.rateC || product.mrp || 0,
                  rateA: product.rateA || 0,
                  rateB: product.rateB || 0,
                  rateC: product.rateC || 0,
                  mrp: product.mrp || 0,
                  pts: product.cost || product.purchaseRate || 0,
                  cost: product.cost || 0,
                  purchaseRate: product.purchaseRate || 0,
                  category: product.category || '',
                  manufacturer: product.manufacturer || product.brand || '',
                  stock: product.stock || 0,
                  batch: product.batch ? product.batch.trim() : '',
                  expiry: product.expiry || null,
                  unit: product.unit ? product.unit.trim() : ''
                };
              }
            }
          }
        } catch (error) {
        }
      }

      // Populate the form with item data, including new fields from product if available
      setNewProductForm({
        ...itemToEdit,
        // Use stock from product if available (latest), otherwise use saved stock, default to 0
        stock: product?.stock !== undefined ? product.stock : (itemToEdit.stock || 0),
        cost: product?.cost || itemToEdit.cost || 0,
        purchaseRate: product?.purchaseRate || itemToEdit.purchaseRate || 0,
        rateA: product?.rateA || itemToEdit.rateA || 0,
        rateB: product?.rateB || itemToEdit.rateB || 0,
        rateC: product?.rateC || itemToEdit.rateC || 0,
        composition: product?.composition || itemToEdit.composition || '',
        // Ensure MRP is set correctly (mrp or rateA/rateB/rateC)
        mrp: itemToEdit.mrp || product?.mrp || product?.rateA || product?.rateB || product?.rateC || 0
      });
      setProductFormErrors({});

      // Set flag to prevent API call when setting search text from edit
      isSettingProductFromSelectionRef.current = true;
      if (product) {
        const productName = product.name || product.productName || '';
        const productCode = product.productCode || product.sku || '';
        setProductSearch(productCode ? `${productName} (${productCode})` : productName);
      } else {
        setProductSearch(itemToEdit.productName || '');
      }
      setIsProductDropdownOpen(false);
      setShowAddProductModal(true);
    }
  };

  const handleAddProduct = () => {
    // Reset form for new product
    setNewProductForm({
      id: Date.now(), // Temporary ID for new items
      productId: '',
      productName: '',
      composition: '',
      sku: '',
      quantity: 1,
      mrp: 0,
      unitPrice: 0,
      unitPricePTS: 0,
      batch: '',
      expiry: '',
      manufacturing: '',
      discount: 0,
      // No default tax; user will enter if needed
      tax: 0,
      stock: 0,
      cost: 0,
      purchaseRate: 0,
      rateA: 0,
      rateB: 0,
      rateC: 0
    });
    setProductFormErrors({});
    setProductSearch('');
    setDebouncedProductSearch(''); // Reset debounced search
    setIsProductDropdownOpen(false); // Don't open dropdown automatically
    setSelectedProductIndex(-1); // Reset selected index
    setShowAddProductModal(true);

    // Focus search input after modal opens
    setTimeout(() => {
      if (productSearchInputRef.current) {
        productSearchInputRef.current.focus();
      }
    }, 150);
  };
  const handleProductFormChange = (field, value, productObject = null) => {
    if (field === 'productId') {
      // Use provided product object if available, otherwise look it up in catalog
      const product = productObject || productCatalog.find(p =>
        p.id === parseInt(value) ||
        p.id === value ||
        p.productId === value ||
        p._id === value
      );
      if (product) {
        // Map fields according to specification:
        // Product Name → productName
        // SKU → productCode
        // Unit Price → rateA
        // PTS Price → cost
        // Batch Number → batch (trimmed)
        // Expiry Date → expiry (if present, else null)
        // MRP → not available (set to 0)
        // Manufacturer → not available (set to empty)
        // Tax % → not available (set to 0)
        // Discount % → user input (keep existing value)
        // Quantity → user input (keep existing value, but validate with stock)

        const productName = (product.productName || product.name || '').trim();
        const productCode = (product.productCode || product.sku || '').trim();
        const rateA = product.rateA || 0;
        const rateB = product.rateB || 0;
        const rateC = product.rateC || 0;
        const cost = product.cost || 0;
        const purchaseRate = product.purchaseRate || 0;
        const mrp = product.mrp || 0;
        const batch = product.batch ? product.batch.trim() : '';
        const expiry = product.expiry || null;
        const stock = product.stock || 0;
        const manufacturer = product.manufacturer || product.brand || '';

        // Format expiry date for display (YYYY-MM-DD)
        const expiryDate = expiry ? new Date(expiry).toISOString().split('T')[0] : '';

        // Determine MRP to display: mrp if available, otherwise rateA, rateB, or rateC
        const displayMRP = mrp || rateA || rateB || rateC || 0;

        const updatedForm = {
          ...newProductForm,
          productId: value,
          productName: productName,
          composition: product.composition || '',
          sku: productCode,
          unitPrice: rateA, // Unit Price shows rateA
          unitPricePTS: cost, // PTS Price shows cost
          batch: batch,
          expiry: expiryDate,
          mrp: displayMRP, // Store MRP for display (mrp or rateA/rateB/rateC)
          manufacturing: manufacturer,
          tax: product.tax || 0, // Auto-fill tax from product data
          stock: stock, // Store stock for validation
          // Store additional fields for display
          cost: cost, // Actual Rate
          purchaseRate: purchaseRate, // Purchase Rate
          rateA: rateA,
          rateB: rateB,
          rateC: rateC,
          // Keep existing quantity and discount (user inputs)
          quantity: newProductForm.quantity || 1,
          discount: newProductForm.discount || 0
        };

        // Clear any previous errors
        const newErrors = { ...productFormErrors };
        delete newErrors.quantity;
        delete newErrors.productId; // Clear duplicate product error when selecting different product

        // Clear MRP and unit price errors since MRP is not available
        delete newErrors.unitPrice;
        delete newErrors.mrp;

        setProductFormErrors(newErrors);
        setNewProductForm(updatedForm);
      } else {
        setNewProductForm({ ...newProductForm, [field]: value });
      }
    } else {
      const updatedForm = { ...newProductForm, [field]: value };

      // Validate based on field type
      const newErrors = { ...productFormErrors };

      if (field === 'quantity') {
        // Value is already capped in onChange handler, just validate and update
        // Convert empty string to 0 for numeric operations
        const numValue = value === '' || value === null || value === undefined ? 0 : Number(value);

        // Only set error if value is clearly invalid (negative or NaN)
        if (isNaN(numValue) || numValue < 0) {
          // Don't update the form if value is invalid
          return;
        }

        // Validate minimum quantity
        if (numValue < NUMBER_LIMITS.quantity.min) {
          newErrors.quantity = `Quantity must be at least ${NUMBER_LIMITS.quantity.min}`;
        } else {
          // Value is valid (already capped in onChange), clear errors
          delete newErrors.quantity;
        }

        // Update the form value
        updatedForm.quantity = numValue;
      } else if (field === 'unitPrice') {
        // Clear any previous errors
        delete newErrors.unitPrice;
        delete newErrors.mrp;
      } else if (field === 'mrp') {
        // Clear any previous errors
        delete newErrors.unitPrice;
        delete newErrors.mrp;
      } else {
        // Clear error for other fields
        delete newErrors[field];
      }

      setProductFormErrors(newErrors);
      setNewProductForm(updatedForm);
    }
  };

  const validateProductForm = () => {
    const newErrors = {};

    // Validate quantity - should not exceed available stock
    if (!newProductForm.quantity || newProductForm.quantity <= 0) {
      newErrors.quantity = 'Please enter quantity greater than 0.';
    } else {
      const stock = newProductForm.stock || 0;
      if (stock > 0 && newProductForm.quantity > stock) {
        newErrors.quantity = `Quantity cannot exceed available stock (${stock})`;
      }
    }

    // Validate discount - must be >= 0 and <= 100
    const discount = newProductForm.discount || 0;
    if (discount < 0 || discount > 100) {
      newErrors.discount = 'Discount must be between 0 and 100.';
    }

    // Validate tax - must be >= 0 and <= 100
    const tax = newProductForm.tax || 0;
    if (tax < 0 || tax > 100) {
      newErrors.tax = 'Tax must be between 0 and 100.';
    }

    setProductFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleConfirmAddProduct = () => {
    if (!validateProductForm()) {
      return;
    }

    const itemSubtotal = newProductForm.quantity * newProductForm.unitPrice;
    const itemDiscount = (itemSubtotal * (newProductForm.discount || 0)) / 100;
    const itemAfterDiscount = itemSubtotal - itemDiscount;
    const itemTax = (itemAfterDiscount * (newProductForm.tax || 0)) / 100;
    const lineTotal = itemAfterDiscount + itemTax;

    const newItem = {
      id: newProductForm.id || Date.now(),
      productId: newProductForm.productId || null,
      productName: newProductForm.productName,
      composition: newProductForm.composition || '',
      sku: newProductForm.sku || '',
      quantity: newProductForm.quantity,
      unitPrice: newProductForm.unitPrice,
      mrp: newProductForm.mrp || 0,
      unitPricePTS: newProductForm.unitPricePTS || 0,
      batch: newProductForm.batch || '',
      expiry: newProductForm.expiry || '',
      manufacturing: newProductForm.manufacturing || '',
      discount: newProductForm.discount || 0,
      // Use the tax the user entered; do not default to 18%
      tax: newProductForm.tax || 0,
      lineTotal: Math.round(lineTotal * 100) / 100,
      priceOverride: newProductForm.priceOverride || false,
      overrideReason: newProductForm.overrideReason || '',
      // Save stock for validation when editing
      stock: newProductForm.stock || 0
    };

    // Check if we're editing an existing item or adding a new one
    const existingItemIndex = quoteItems.findIndex(item => item.id === newItem.id);
    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...quoteItems];
      updatedItems[existingItemIndex] = newItem;
      setQuoteItems(updatedItems);
      // Close modal after updating (editing should close modal)
      setShowAddProductModal(false);
      setProductFormErrors({});
      setProductSearch('');
      setIsProductDropdownOpen(false);
      setSelectedProductIndex(-1);
      wasInputFocusedRef.current = false;
    } else {
      // Check if product is already added (by productId or productName)
      const isDuplicate = quoteItems.some(item => {
        // If both have productId, compare by productId
        if (newItem.productId && item.productId) {
          return String(item.productId) === String(newItem.productId);
        }
        // Otherwise, compare by productName (case-insensitive)
        if (newItem.productName && item.productName) {
          return item.productName.toLowerCase().trim() === newItem.productName.toLowerCase().trim();
        }
        return false;
      });

      if (isDuplicate) {
        // Show error message
        setProductFormErrors({
          productId: 'This product is already added to the quotation. Please edit the existing item instead.'
        });
        // Focus on the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
          }
        }, 100);
        return;
      }

      // Add new item - keep modal open for adding more products
      setQuoteItems([...quoteItems, newItem]);

      // Reset form for next product (but keep modal open)
      setNewProductForm({
        id: Date.now(), // New temporary ID for next item
        productId: '',
        productName: '',
        composition: '',
        sku: '',
        quantity: 1,
        mrp: 0,
        unitPrice: 0,
        unitPricePTS: 0,
        batch: '',
        expiry: '',
        manufacturing: '',
        discount: 0,
        tax: 0,
        stock: 0,
        cost: 0,
        purchaseRate: 0,
        rateA: 0,
        rateB: 0,
        rateC: 0
      });
      setProductFormErrors({});
      setProductSearch('');
      setDebouncedProductSearch('');
      setIsProductDropdownOpen(false);
      setSelectedProductIndex(-1);

      // Focus the search input after adding product
      setTimeout(() => {
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
        }
      }, 200);
    }
  };
  const handleCancelAddProduct = () => {
    setShowAddProductModal(false);
    setProductFormErrors({});
    setProductSearch('');
    setIsProductDropdownOpen(false);
    setSelectedProductIndex(-1);
    wasInputFocusedRef.current = false;
  };

  // Handle product selection
  const handleSelectProduct = (product) => {
    const productName = product.name || product.productName || '';
    const productCode = product.productCode || product.sku || '';
    const displayText = productCode ? `${productName} (${productCode})` : productName;
    const selectedProductId = product.id || product._id || product.productId;

    if (selectedProductId) {
      // Pass the full product object to avoid lookup issues
      handleProductFormChange('productId', selectedProductId, product);
      setIsProductDropdownOpen(false);
      // Set flag to prevent API call when setting search text from selection
      isSettingProductFromSelectionRef.current = true;
      setProductSearch(displayText);
      setSelectedProductIndex(-1);
    }
  };

  // Handle keyboard navigation in product dropdown
  const handleProductSearchKeyDown = (e) => {
    // Block restricted symbols from being entered
    const restrictedSymbols = ['-', '<', '>', '/', "'", '"', '(', ')', '=', '`', '%', '&', ';', ':', '#', '\\', '+', '{', '}', '[', ']', '|', '@', '$', '*', ',', '.', '_', '~'];
    if (restrictedSymbols.includes(e.key)) {
      e.preventDefault();
      return;
    }

    // If Enter is pressed and a product is already selected, add the product
    if (e.key === 'Enter' && newProductForm.productId && !isProductDropdownOpen) {
      e.preventDefault();
      handleConfirmAddProduct();
      return;
    }

    if (!isProductDropdownOpen && productCatalog.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsProductDropdownOpen(true);
        setSelectedProductIndex(e.key === 'ArrowDown' ? 0 : productCatalog.length - 1);
        e.preventDefault();
        return;
      }
    }

    if (!isProductDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedProductIndex(prev =>
          prev < productCatalog.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedProductIndex(prev =>
          prev > 0 ? prev - 1 : productCatalog.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedProductIndex >= 0 && selectedProductIndex < productCatalog.length) {
          handleSelectProduct(productCatalog[selectedProductIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsProductDropdownOpen(false);
        setSelectedProductIndex(-1);
        break;
    }
  };

  // Handle Enter key in modal inputs to add product
  const handleModalInputKeyDown = (e) => {
    if (e.key === 'Enter' && newProductForm.productId) {
      e.preventDefault();
      handleConfirmAddProduct();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showAddProductModal) {
        setShowAddProductModal(false);
        setProductFormErrors({});
        setProductSearch('');
        setIsProductDropdownOpen(false);
        setSelectedProductIndex(-1);
        wasInputFocusedRef.current = false;
      }
    };

    if (showAddProductModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showAddProductModal]);

  const handleRemoveItem = (itemId) => {
    setQuoteItems(quoteItems.filter(item => item.id !== itemId));
  };

  const handleItemChange = (itemId, field, value) => {
    setQuoteItems(quoteItems.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'productId') {
          const product = productCatalog.find(p =>
            p.id === parseInt(value) ||
            p.id === value ||
            p.productId === value ||
            p._id === value
          );
          if (product) {
            updatedItem.productName = product.name;
            updatedItem.sku = product.sku || '';
            updatedItem.unitPrice = product.price || 0;
            updatedItem.mrp = product.mrp || product.price || 0;
            updatedItem.unitPricePTS = product.pts || 0;
            updatedItem.manufacturing = product.manufacturer || '';
          }
        }
        updatedItem.lineTotal = calculateLineTotal(updatedItem);
        return updatedItem;
      }
      return item;
    }));
  };

  const processFiles = async (files) => {
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

    if (files.length === 0) {
      return;
    }

    setIsUploadingAttachment(true);
    setUploadError('');

    try {
      for (const file of files) {
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File ${file.name} exceeds 25MB limit`);
          continue;
        }

        // Set initial progress
        setUploadProgress(0);

        // Prepare FormData for immediate upload
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('folder', 'quotations');

        // Emulate progress since axios progress isn't directly exposed in the API client wrapper
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) return prev;
            return prev + 10;
          });
        }, 500);

        const result = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, uploadFormData);

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (result.responseCode === 200) {
          const newAttachment = {
            id: result.data.public_id || Date.now() + Math.random(),
            name: file.name,
            filename: file.name,
            size: file.size,
            mimeType: file.type,
            type: file.type,
            url: result.data.url,
            public_id: result.data.public_id,
            isExisting: false, // We treat newly uploaded files as ready to be saved with the quote
            uploadedAt: new Date().toISOString()
          };

          setAttachments(prev => [...prev, newAttachment]);
          toast.success(`File ${file.name} uploaded successfully`);
        } else {
          toast.error(result.responseMessage || `Failed to upload ${file.name}`);
        }

        // Reset progress after a brief delay
        setTimeout(() => setUploadProgress(0), 1000);
      }
    } catch (error) {
      toast.error('An error occurred during file upload');
      setUploadProgress(0);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    await processFiles(files);
    // Reset input to allow selecting same file again
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    await processFiles(files);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(file => file.id !== fileId));
  };

  const removeAttachment = async (index) => {
    const attachment = attachments[index];
    const quoteId = currentQuoteId || quotationIdFromQuery;

    if (attachment.isExisting && quoteId) {
      // It's already saved on the server-side quotation
      try {
        const result = await quotationService.deleteAttachment(quoteId, attachment.id);
        if (result.success) {
          setAttachments(attachments.filter((_, i) => i !== index));
          toast.success('Attachment deleted successfully');
        } else {
          toast.error(result.error || 'Failed to delete attachment');
        }
      } catch (error) {
        toast.error('An error occurred while deleting the attachment');
      }
    } else {
      // It's pre-uploaded to Cloudinary but not yet saved in a quotation
      // Or it's a local file (though now all files are uploaded immediately)
      if (attachment.public_id) {
        try {
          // Attempt to delete from Cloudinary
          await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${attachment.public_id}&resource_type=raw`);
        } catch (error) {
          // We still remove it from state even if Cloudinary delete fails
        }
      }

      setAttachments(attachments.filter((_, i) => i !== index));
      if (!attachment.isExisting && attachment.public_id) {
        toast.success('File removed');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const ensureFilenameHasExtension = (filename, mimeType = '') => {
    if (!filename) return 'attachment';

    const mimeToExtension = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp',
      'text/plain': '.txt'
    };

    if (/\.[a-z0-9]+$/i.test(filename)) return filename;
    return `${filename}${mimeToExtension[mimeType] || ''}`;
  };

  const filenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const last = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-z0-9]+$/i.test(last) ? last : '';
    } catch {
      return '';
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!attachment?.url) {
      toast.error('No attachment URL found');
      return;
    }

    const baseName =
      attachment.originalName ||
      attachment.filename ||
      attachment.name ||
      filenameFromUrl(attachment.url) ||
      'attachment';

    try {
      const response = await authFetch(attachment.url);
      if (!response.ok) throw new Error('Failed to fetch attachment');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const finalName = ensureFilenameHasExtension(baseName, blob.type || attachment.mimeType || attachment.type || '');

      triggerBrowserDownload(blobUrl, finalName);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      // Fallback for CORS-restricted URLs
      triggerBrowserDownload(attachment.url, ensureFilenameHasExtension(baseName, attachment.mimeType || attachment.type || ''));
    }
  };

  // Save the quote as draft
  const handleSaveDraft = async () => {
    // Validate required fields
    if (!quoteDetails.title || quoteDetails.title.trim() === '') {
      setErrors({ title: 'Quote title is required' });
      return;
    }

    if (!quoteDetails.expiryDate || quoteDetails.expiryDate.trim() === '') {
      setErrors({ expiryDate: 'Expiry date is required' });
      return;
    }

    if (!quoteDetails.email || quoteDetails.email.trim() === '') {
      setErrors({ email: 'Email is required' });
      return;
    }

    // Validate that expiry date is in the future
    const expiryDate = new Date(quoteDetails.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiryDate <= today) {
      setErrors({ expiryDate: 'Expiry date must be in the future' });
      return;
    }

    if (quoteItems.length === 0) {
      alert('Please add at least one product to the quote');
      return;
    }

    // No longer require opportunity or customer - user can fill manually

    setErrors({});

    const totals = calculateTotals();
    const quoteData = {
      // Include opportunityId if it exists
      ...(opportunity && opportunity._id ? { opportunityId: opportunity._id || opportunity.id } : {}),
      // Include lead fields
      leadId: opportunity?.leadId?._id || leadData?._id || (typeof opportunity?.leadId === 'string' ? opportunity.leadId : null),
      leadReference: opportunity?.leadId?.leadId || leadData?.leadId || null,
      // Include customerId if customer is selected
      ...(customer && customer._id ? { customerId: customer._id || customer.id } : {}),
      title: quoteDetails.title,
      expiryDate: quoteDetails.expiryDate,
      email: quoteDetails.email,
      terms: quoteDetails.terms || '',
      notes: quoteDetails.notes || '',
      priority: quoteDetails.priority || 'normal',
      billingAddress: quoteAddresses.billingAddress,
      shippingAddress: quoteAddresses.shippingAddress,
      items: quoteItems.map(item => {
        // Recalculate lineTotal to ensure it matches what's displayed
        const lineTotal = calculateLineTotal(item);
        return {
          productId: sanitizeProductId(item.productId),
          productName: item.productName,
          composition: item.composition || '',
          sku: item.sku || '',
          description: '',
          quantity: item.quantity,
          mrp: Math.round((item.mrp || 0) * 100) / 100,
          unitPrice: Math.round(item.unitPrice * 100) / 100,
          unitPricePTS: Math.round((item.unitPricePTS || 0) * 100) / 100,
          batch: item.batch || '',
          expiry: item.expiry || null,
          manufacturing: item.manufacturing || '',
          discount: Math.round((item.discount || 0) * 100) / 100,
          // Use tax only if user entered it; default to 0 when not provided
          tax: Math.round((item.tax ?? 0) * 100) / 100,
          lineTotal: lineTotal,
          priceOverride: item.priceOverride || false,
          overrideReason: item.overrideReason || ''
        };
      }),
      // Include totals to match what's displayed on page
      subtotal: totals.subtotal,
      totalDiscount: totals.totalDiscount,
      taxableAmount: totals.taxableAmount,
      totalTax: totals.totalTax,
      grandTotal: totals.grandTotal
    };

    saveDraftMutation.mutate({ quoteId: currentQuoteId, quoteData });
  };

  const handleCreateQuote = async () => {
    // Basic validation
    if (!quoteDetails.title?.trim()) {
      alert('Quote title is required');
      return;
    }

    if (!quoteDetails.expiryDate || quoteDetails.expiryDate.trim() === '') {
      alert('Expiry date is required');
      return;
    }

    if (!quoteDetails.email || quoteDetails.email.trim() === '') {
      alert('Email is required');
      return;
    }

    // Validate that expiry date is in the future
    const expiryDate = new Date(quoteDetails.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiryDate <= today) {
      alert('Expiry date must be in the future');
      return;
    }

    if (quoteItems.length === 0) {
      alert('Please add at least one product');
      return;
    }

    const isUpdate = !!(currentQuoteId || quotationIdFromQuery);

    // No longer require opportunity or customer - user can fill manually

    try {
      const formData = new FormData();

      // Include opportunityId when provided (for both create and update)
      if (opportunity && opportunity._id) {
        formData.append('opportunityId', opportunity._id);
      }

      // Include customerId when provided (for both create and update)
      if (customer && customer._id) {
        formData.append('customerId', customer._id);
      }

      // Include lead fields
      const leadObjectId = opportunity?.leadId?._id || leadData?._id || (typeof opportunity?.leadId === 'string' ? opportunity.leadId : null);
      const leadRef = opportunity?.leadId?.leadId || leadData?.leadId || null;

      if (leadObjectId) formData.append('leadId', leadObjectId);
      if (leadRef) formData.append('leadReference', leadRef);

      formData.append('title', quoteDetails.title);
      formData.append('expiryDate', quoteDetails.expiryDate);
      formData.append('email', quoteDetails.email || '');
      formData.append('terms', quoteDetails.terms || '');
      formData.append('notes', quoteDetails.notes || '');
      formData.append('priority', quoteDetails.priority || 'normal');
      formData.append('status', quoteStatus || 'draft');

      // Calculate totals to match what's displayed on page
      const totals = calculateTotals();

      // ITEMS - Send as JSON string (backend will parse it)
      // Round all amounts to 2 decimal places to match what's displayed
      const itemsArray = quoteItems.map((item) => {
        // Recalculate lineTotal to ensure it matches what's displayed
        const lineTotal = calculateLineTotal(item);
        return {
          productId: sanitizeProductId(item.productId),
          productName: item.productName,
          composition: item.composition || '',
          sku: item.sku || '',
          quantity: item.quantity,
          mrp: Math.round((item.mrp || 0) * 100) / 100,
          unitPrice: Math.round(item.unitPrice * 100) / 100,
          unitPricePTS: Math.round((item.unitPricePTS || 0) * 100) / 100,
          batch: item.batch || '',
          expiry: item.expiry || '',
          manufacturing: item.manufacturing || '',
          discount: Math.round((item.discount || 0) * 100) / 100,
          tax: Math.round((item.tax || 0) * 100) / 100,
          lineTotal: lineTotal,
          description: '' // Add description field
        };
      });
      formData.append('items', JSON.stringify(itemsArray));

      // Include totals to match what's displayed on page
      formData.append('subtotal', totals.subtotal.toString());
      formData.append('totalDiscount', totals.totalDiscount.toString());
      formData.append('taxableAmount', totals.taxableAmount.toString());
      formData.append('totalTax', totals.totalTax.toString());
      formData.append('grandTotal', totals.grandTotal.toString());

      // BILLING & SHIPPING ADDRESSES - Send as JSON strings
      formData.append('billingAddress', JSON.stringify(quoteAddresses.billingAddress));
      formData.append('shippingAddress', JSON.stringify(quoteAddresses.shippingAddress));

      // ATTACHMENTS - Now sending metadata because files are pre-uploaded
      // We only send attachments that have a public_id (pre-uploaded or existing)
      const attachmentMetadata = attachments.map(att => ({
        url: att.url,
        public_id: att.public_id,
        mimeType: att.mimeType || att.type,
        size: att.size,
        filename: att.filename || att.name
      }));
      formData.append('attachments', JSON.stringify(attachmentMetadata));

      const quoteId = currentQuoteId || quotationIdFromQuery;
      createUpdateQuotationMutation.mutate({ isUpdate, quoteId, formData });
    } catch (error) {
      alert(error.message || (isUpdate ? 'Failed to update quotation' : 'Failed to create quotation'));
    }
  };



  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/crm/quotation-proposal/dashboard');
    }
  };

  // Check if all required fields are filled
  const isAllRequiredFieldsFilled = () => {
    // 1. Check quote details - Quote Title (required)
    if (!quoteDetails.title || !quoteDetails.title.trim()) {
      return false;
    }

    // 2. Check quote details - Expiry Date (required and must be in future)
    if (!quoteDetails.expiryDate || quoteDetails.expiryDate.trim() === '') {
      return false;
    }

    // Check Email
    if (!quoteDetails.email || !quoteDetails.email.trim()) {
      return false;
    }

    // Validate that expiry date is in the future
    try {
      const expiryDate = new Date(quoteDetails.expiryDate);
      if (isNaN(expiryDate.getTime())) {
        return false; // Invalid date
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);
      if (expiryDate <= today) {
        return false; // Expiry date must be in the future
      }
    } catch (error) {
      return false; // Invalid date format
    }

    // 3. Check billing address - All required fields
    const billing = quoteAddresses.billingAddress;
    const isValueFilled = (val) => {
      if (!val) return false;
      if (typeof val === 'string') return val.trim().length > 0;
      if (typeof val === 'object') return true; // Non-null object is valid
      return true;
    };

    if (!billing.company || !billing.company.trim()) return false;
    if (!billing.address || !billing.address.trim()) return false;
    if (!isValueFilled(billing.city)) return false;
    if (!isValueFilled(billing.state)) return false;
    if (!billing.pincode || billing.pincode.toString().trim() === '' || billing.pincode.toString().trim().length < 3) return false;

    // 4. Check shipping address - All required fields
    const shipping = quoteAddresses.shippingAddress;
    if (!shipping.company || !shipping.company.trim()) return false;
    if (!shipping.address || !shipping.address.trim()) return false;
    if (!isValueFilled(shipping.city)) return false;
    if (!isValueFilled(shipping.state)) return false;
    if (!shipping.pincode || shipping.pincode.toString().trim() === '' || shipping.pincode.toString().trim().length < 3) return false;
    // Phone was optional in some contexts but required here previously. Keeping it checking length if present? 
    // Wait, the previous code had: if (!shipping.phone || !shipping.phone.trim() || shipping.phone.trim().length < 10) return false;
    // I should strictly keep it as required if it was before.
    const phoneStr = (shipping.phone && Array.isArray(shipping.phone)) ? (shipping.phone[0] || '') : (shipping.phone || '');
    if (!phoneStr || !phoneStr.toString().trim() || phoneStr.toString().trim().length < 10) return false;


    // 5. Check quote items - Must have at least one item
    if (!quoteItems || !Array.isArray(quoteItems) || quoteItems.length === 0) {
      return false;
    }

    // 6. Check if all items have required fields
    for (const item of quoteItems) {
      // Product name is required
      if (!item.productName || !item.productName.trim()) {
        return false;
      }

      // Quantity must be a positive number
      if (!item.quantity || item.quantity <= 0 || isNaN(item.quantity)) {
        return false;
      }

      // Unit price must be defined and non-negative
      if (item.unitPrice === undefined || item.unitPrice === null || isNaN(item.unitPrice) || item.unitPrice < 0) {
        return false;
      }
    }

    return true;
  };


  const handlePriceOverride = (itemId, override) => {
    setQuoteItems(quoteItems.map(item => {
      if (item.id === itemId) {
        return { ...item, priceOverride: override };
      }
      return item;
    }));
  };


  const totals = calculateTotals();

  // Reusable header component (defined once, used in all conditional returns)
  const pageHeader = (
    <div className="flex items-center space-x-4">
      <Button
        variant="outline"
        icon={<ArrowLeft className="h-4 w-4" />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Quote Builder</h1>
        <p className="text-gray-600">Create and customize your quotation</p>
      </div>
    </div>
  );

  // Show loading state first (if we have an opportunityId/customerId/quotationId but haven't loaded yet)
  // This prevents showing error page during initial load
  if (loadingOpportunity ||
    (finalOpportunityId && !opportunity && !errorOpportunity) ||
    (customerIdFromQuery && !customer && !errorOpportunity) ||
    (quotationIdFromQuery && !quoteStatus && !errorOpportunity)) {
    return (
      <div className="space-y-6">
        {pageHeader}

        <div className="bg-white border border-gray-200 rounded-lg p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Removed the "Opportunity Required" error - users can now create quotes without opportunity or customer

  // Show error state
  if (errorOpportunity) {
    return (
      <div className="space-y-6">
        {pageHeader}

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error Loading Opportunity</h3>
              <p className="text-sm text-red-700 mt-1">{errorOpportunity}</p>
              <div className="flex items-center space-x-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setErrorOpportunity(null);
                    if (finalOpportunityId && refetchOpportunity) {
                      refetchOpportunity();
                    }
                  }}
                >
                  Retry
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/crm/quotation-proposal/create-quotes')}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Select Different Opportunity
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Removed the "No Opportunity Data" error - users can now create quotes without opportunity or customer

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        {pageHeader}
      </div>

      {/* Opportunity/Customer Info - Read Only (Auto-filled from API) */}
      {(opportunity || customer) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {customer ? 'Customer Information' : 'Opportunity Information'}
          </h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-start space-x-3 flex-1 min-w-[200px]">
              <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-1">Company Name</p>
                <p
                  className="font-medium text-gray-900 break-words capitalize"
                  title={
                    customer?.companyName ||
                    leadData?.company ||
                    quoteAddresses.billingAddress.company ||
                    opportunity?.leadId?.company ||
                    'N/A'
                  }
                >
                  {customer?.companyName ||
                    leadData?.company ||
                    quoteAddresses.billingAddress.company ||
                    opportunity?.leadId?.company ||
                    'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 flex-1 min-w-[200px]">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                {(() => {
                  const contactName = customer?.contactPerson
                    ? customer.contactPerson
                    : contact?.firstName && contact?.lastName
                      ? `${contact.firstName} ${contact.lastName}`.trim()
                      : contact?.firstName || contact?.lastName
                        ? (contact.firstName || contact.lastName)
                        : opportunity?.leadId?.firstName || opportunity?.leadId?.lastName
                          ? `${opportunity.leadId.firstName || ''} ${opportunity.leadId.lastName || ''}`.trim()
                          : quoteAddresses.shippingAddress.contact ||
                          opportunity?.stakeholders?.[0]?.name ||
                          'N/A';
                  const contactEmail = customer?.email || contact?.email || opportunity?.leadId?.email;

                  return (
                    <>
                      <p
                        className="font-medium text-gray-900 break-words capitalize"
                        title={contactName}
                      >
                        {contactName}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quote Title <span className="text-red-500">*</span>
            </label>
            <div className="space-y-1">
              <input
                type="text"
                value={quoteDetails.title}
                onChange={(e) => {
                  const sanitized = handleTextInputChange(e, null, 100);
                  setQuoteDetails({ ...quoteDetails, title: sanitized });
                  if (errors.title) {
                    setErrors(prev => ({ ...prev, title: '' }));
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                maxLength={100}
                required
              />
              <div className="text-right text-xs text-gray-500">
                {getCharacterCount(quoteDetails.title, 100)}
              </div>
            </div>
            {errors.title && (
              <p className="text-xs text-red-600 mt-1">{errors.title}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={quoteDetails.expiryDate}
              min={(() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                return tomorrow.toISOString().split('T')[0];
              })()}
              onChange={(e) => {
                const selectedDate = e.target.value;
                setQuoteDetails({ ...quoteDetails, expiryDate: selectedDate });

                // If status is expired and user changes expiry date, reset status to draft
                if (quoteStatus === 'expired') {
                  setQuoteStatus('draft');
                }

                if (errors.expiryDate) {
                  setErrors(prev => ({ ...prev, expiryDate: '' }));
                }
                // Validate that date is in the future
                if (selectedDate) {
                  const expiryDate = new Date(selectedDate);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (expiryDate <= today) {
                    setErrors(prev => ({ ...prev, expiryDate: 'Expiry date must be in the future' }));
                  }
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.expiryDate ? 'border-red-300' : 'border-gray-300'
                }`}
              required
            />
            {errors.expiryDate && (
              <p className="text-xs text-red-600 mt-1">{errors.expiryDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="space-y-1">
              <input
                type="email"
                value={quoteDetails.email}
                onChange={(e) => {
                  setQuoteDetails({ ...quoteDetails, email: e.target.value.slice(0, 100) });
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: '' }));
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                maxLength={100}
                placeholder="Enter email address"
                required
              />
              <div className="text-right text-xs flex items-center justify-between text-gray-500">
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )} <span className="ml-auto">{getCharacterCount(quoteDetails.email, 100)}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={quoteStatus}
              onChange={(e) => setQuoteStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white capitalize"
            >
              {['draft', 'sent', 'seen', 'accepted', 'rejected', 'expired'].map((status) => (
                <option key={status} value={status}>
                  {formatQuotationStatus(status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority
            </label>
            <div className="flex bg-gray-50 p-1 rounded-lg border border-gray-200">
              {['low', 'normal', 'high'].map((priority) => (
                <button
                  key={priority}
                  type="button"
                  onClick={() => setQuoteDetails(prev => ({ ...prev, priority }))}
                  className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-all duration-200 capitalize ${quoteDetails.priority === priority
                      ? priority === 'high'
                        ? 'bg-red-500 text-white shadow-sm'
                        : priority === 'normal'
                          ? 'bg-agrosaf-green text-white shadow-sm'
                          : 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {priority}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>


      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'products', label: 'Products & Services', icon: Package },
              { key: 'billing', label: 'Billing Address', icon: Building },
              { key: 'shipping', label: 'Shipping Address', icon: Truck },
              { key: 'terms', label: 'Terms & Conditions', icon: FileText },
              { key: 'attachments', label: 'Attachments', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.key
                  ? 'border-agrosaf-green text-agrosaf-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Products & Services Tab */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Products & Services</h3>
                <div className="flex items-center space-x-3">
                  {loadingProducts && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                      <span>Loading products...</span>
                    </div>
                  )}
                  <Button
                    variant="primary"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={handleAddProduct}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    disabled={loadingProducts}
                  >
                    Add Product
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quoteItems.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                          No products added yet. Click "Add Product" to get started.
                        </td>
                      </tr>
                    ) : (
                      quoteItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 break-words text-sm font-medium text-gray-900 capitalize">
                            {item.productName || getProductNameById(item.productId) || '-'}
                            {(() => {
                              const composition = item.composition || getProductCompositionById(item.productId);
                              if (!composition) return null;

                              return (
                                <p className="mt-1 text-xs font-normal text-gray-500 normal-case">
                                  {composition}
                                </p>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">{item.quantity}</td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">₹{item.mrp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">
                            ₹{item.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {item.priceOverride && <span className="text-red-600 text-xs ml-1">(Override)</span>}
                          </td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">{formatDateDDMMYYYY(item.expiry)}</td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">{item.manufacturing || '-'}</td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">{item.discount}%</td>
                          <td className="px-4 py-3 break-words text-sm text-gray-600">{item.tax}%</td>
                          <td className="px-4 py-3 break-words text-sm font-medium text-gray-900">₹{item.lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 break-words text-sm font-medium">
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="h-4 w-4" />}
                                onClick={() => handleEditItem(item.id)}
                              ></Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => handleRemoveItem(item.id)}
                              ></Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Discount:</span>
                    <span className="font-medium text-green-600">-₹{totals.totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxable Amount:</span>
                    <span className="font-medium">₹{totals.taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tax:</span>
                    <span className="font-medium">₹{totals.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-lg font-semibold text-gray-900">Grand Total:</span>
                    <span className="text-lg font-bold text-agrosaf-green">₹{totals.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Address Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Billing Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.billingAddress.company}
                    onChange={(e) => {
                      const value = handleTextInputChange(e, null, FIELD_LIMITS.company);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, company: value }
                      }));
                      if (errors.billingCompany) {
                        setErrors(prev => ({ ...prev, billingCompany: '' }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize ${errors.billingCompany ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter company name"
                    maxLength={FIELD_LIMITS.company}
                    required
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.billingAddress.company, FIELD_LIMITS.company)}
                    </span>
                  </div>
                  {errors.billingCompany && (
                    <p className="text-xs text-red-600 mt-1">{errors.billingCompany}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.billingAddress.gstin}
                    onChange={(e) => {
                      const value = handleTextInputChange(e, null, FIELD_LIMITS.gstin);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, gstin: value.toUpperCase() }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    maxLength={FIELD_LIMITS.gstin}
                    placeholder="Enter GSTIN (optional)"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.billingAddress.gstin, FIELD_LIMITS.gstin)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Drug License Number
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.billingAddress.drugLicenseNumber}
                    onChange={(e) => {
                      const value = handleTextInputChange(e, null, FIELD_LIMITS.drugLicenseNumber);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, drugLicenseNumber: value.toUpperCase() }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    maxLength={FIELD_LIMITS.drugLicenseNumber}
                    placeholder="Enter Drug License Number (optional)"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.billingAddress.drugLicenseNumber, FIELD_LIMITS.drugLicenseNumber)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={quoteAddresses.billingAddress.drugLicenseExpiry}
                    onChange={(e) => {
                      const value = e.target.value;
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, drugLicenseExpiry: value }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    House no / Street <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.billingAddress.address}
                    onChange={(e) => {
                      const value = handleTextInputChange(e, null, FIELD_LIMITS.address);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, address: value }
                      }));
                      if (errors.billingAddress) {
                        setErrors(prev => ({ ...prev, billingAddress: '' }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.billingAddress ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter house number / street"
                    maxLength={FIELD_LIMITS.address}
                    required
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.billingAddress.address, FIELD_LIMITS.address)}
                    </span>
                  </div>
                  {errors.billingAddress && (
                    <p className="text-xs text-red-600 mt-1">{errors.billingAddress}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={billingCountryOptions}
                    value={quoteAddresses.billingAddress.country}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: {
                          ...prev.billingAddress,
                          country: val,
                          state: null,
                          city: null
                        }
                      }));
                    }}
                    placeholder="Select Country"
                    searchPlaceholder="Search country..."
                    getError={() => null}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={billingStateOptions}
                    value={quoteAddresses.billingAddress.state}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: {
                          ...prev.billingAddress,
                          state: val,
                          city: null
                        }
                      }));
                      if (errors.billingState) {
                        setErrors(prev => ({ ...prev, billingState: '' }));
                      }
                    }}
                    placeholder="Select State"
                    searchPlaceholder="Search state..."
                    disabled={!quoteAddresses.billingAddress.country}
                    getError={() => errors.billingState}
                  />
                  {errors.billingState && (
                    <p className="text-xs text-red-600 mt-1">{errors.billingState}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={billingCityOptions}
                    value={quoteAddresses.billingAddress.city}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: {
                          ...prev.billingAddress,
                          city: val
                        }
                      }));
                      if (errors.billingCity) {
                        setErrors(prev => ({ ...prev, billingCity: '' }));
                      }
                    }}
                    placeholder="Select City"
                    searchPlaceholder="Search city..."
                    disabled={!quoteAddresses.billingAddress.state}
                    getError={() => errors.billingCity}
                  />
                  {errors.billingCity && (
                    <p className="text-xs text-red-600 mt-1">{errors.billingCity}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.billingAddress.pincode}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow alphanumeric
                      const sanitized = value.replace(/[^a-zA-Z0-9]/g, '');
                      setQuoteAddresses(prev => ({
                        ...prev,
                        billingAddress: { ...prev.billingAddress, pincode: sanitized.slice(0, 15) }
                      }));
                      if (sanitized.length > 0 && sanitized.length < 3) {
                        setErrors((prev) => ({ ...prev, billingPincode: 'Postal Code must be at least 3 characters' }));
                      } else {
                        if (errors.billingPincode) {
                          setErrors(prev => ({ ...prev, billingPincode: '' }));
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.billingPincode ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter postal code"
                    maxLength={15}
                    required
                  />
                  <div className="text-right text-xs flex items-center justify-between text-gray-500 mt-1">
                    {errors.billingPincode && (
                      <p className="text-xs text-red-600">{errors.billingPincode}</p>
                    )}
                    <span className="ml-auto">{getCharacterCount(quoteAddresses.billingAddress.pincode, 15)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shipping Address Tab */}
          {activeTab === 'shipping' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Shipping Address</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQuoteAddresses(prev => ({
                      ...prev,
                      shippingAddress: {
                        company: prev.billingAddress.company,
                        address: prev.billingAddress.address,
                        landmark: prev.shippingAddress.landmark || '', // Keep existing or empty
                        country: prev.billingAddress.country, // Copy country from billing
                        city: prev.billingAddress.city,
                        state: prev.billingAddress.state,
                        pincode: prev.billingAddress.pincode,
                        contact: prev.shippingAddress.contact || '', // Keep existing or empty
                        phone: prev.shippingAddress.phone || '', // Keep existing or empty
                        deliveryInstructions: prev.shippingAddress.deliveryInstructions || '' // Keep existing or empty
                      }
                    }));
                  }}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300 hover:border-blue-400 font-medium"
                >
                  Copy from Billing
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.shippingAddress.company}
                    onChange={handleShippingCompanyChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize ${errors.shippingCompany ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter company name"
                    maxLength={FIELD_LIMITS.company}
                    required
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.shippingAddress.company, FIELD_LIMITS.company)}
                    </span>
                  </div>
                  {errors.shippingCompany && (
                    <p className="text-xs text-red-600 mt-1">{errors.shippingCompany}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.shippingAddress.contact}
                    onChange={handleShippingContactChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize"
                    placeholder="Enter contact person"
                    maxLength={FIELD_LIMITS.customerName}
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.shippingAddress.contact, FIELD_LIMITS.customerName)}
                    </span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    House no / Street <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.shippingAddress.address}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.address);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: { ...prev.shippingAddress, address: sanitized }
                      }));
                      if (errors.shippingAddress) {
                        setErrors(prev => ({ ...prev, shippingAddress: '' }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.shippingAddress ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter house number / street"
                    maxLength={FIELD_LIMITS.address}
                    required
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.shippingAddress.address, FIELD_LIMITS.address)}
                    </span>
                  </div>
                  {errors.shippingAddress && (
                    <p className="text-xs text-red-600 mt-1">{errors.shippingAddress}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Landmark
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.shippingAddress.landmark}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.address);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: { ...prev.shippingAddress, landmark: sanitized }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Near landmark, building name, etc."
                    maxLength={FIELD_LIMITS.address}
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.shippingAddress.landmark, FIELD_LIMITS.address)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={shippingCountryOptions}
                    value={quoteAddresses.shippingAddress.country}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: {
                          ...prev.shippingAddress,
                          country: val,
                          state: null,
                          city: null
                        }
                      }));
                    }}
                    placeholder="Select Country"
                    searchPlaceholder="Search country..."
                    renderOption={(option) => (
                      <div className="flex items-center gap-2">
                        <span>{option.flag}</span>
                        <span>{option.name}</span>
                      </div>
                    )}
                    getError={() => null}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={shippingStateOptions}
                    value={quoteAddresses.shippingAddress.state}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: {
                          ...prev.shippingAddress,
                          state: val,
                          city: null
                        }
                      }));
                      if (errors.shippingState) {
                        setErrors(prev => ({ ...prev, shippingState: '' }));
                      }
                    }}
                    placeholder="Select State"
                    searchPlaceholder="Search state..."
                    disabled={!quoteAddresses.shippingAddress.country}
                    getError={() => errors.shippingState}
                  />
                  {errors.shippingState && (
                    <p className="text-xs text-red-600 mt-1">{errors.shippingState}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={shippingCityOptions}
                    value={quoteAddresses.shippingAddress.city}
                    onChange={(val) => {
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: {
                          ...prev.shippingAddress,
                          city: val
                        }
                      }));
                      if (errors.shippingCity) {
                        setErrors(prev => ({ ...prev, shippingCity: '' }));
                      }
                    }}
                    placeholder="Select City"
                    searchPlaceholder="Search city..."
                    disabled={!quoteAddresses.shippingAddress.state}
                    getError={() => errors.shippingCity}
                  />
                  {errors.shippingCity && (
                    <p className="text-xs text-red-600 mt-1">{errors.shippingCity}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAddresses.shippingAddress.pincode}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow alphanumeric
                      const sanitized = value.replace(/[^a-zA-Z0-9]/g, '');
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: { ...prev.shippingAddress, pincode: sanitized.slice(0, 15) }
                      }));
                      if (sanitized.length > 0 && sanitized.length < 3) {
                        setErrors((prev) => ({ ...prev, shippingPincode: 'Postal Code must be at least 3 characters' }));
                      } else {
                        if (errors.shippingPincode) {
                          setErrors(prev => ({ ...prev, shippingPincode: '' }));
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.shippingPincode ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter postal code"
                    maxLength={15}
                    required
                  />
                  <div className="text-right text-xs flex items-center justify-between text-gray-500 mt-1">
                    {errors.shippingPincode && (
                      <p className="text-xs text-red-600">{errors.shippingPincode}</p>
                    )}
                    <span className="ml-auto">{getCharacterCount(quoteAddresses.shippingAddress.pincode, 15)}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={quoteAddresses.shippingAddress.phone}
                    onChange={handleShippingPhoneChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.shippingPhone ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter 10 digit phone number"
                    maxLength={FIELD_LIMITS.phone}
                    required
                  />
                  <div className="text-right text-xs flex items-center justify-between text-gray-500 mt-1">
                    {errors.shippingPhone && (
                      <p className="text-xs text-red-600">{errors.shippingPhone}</p>
                    )}
                    <span className="ml-auto">{getCharacterCount(quoteAddresses.shippingAddress.phone, 15)}</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Instructions
                  </label>
                  <textarea
                    rows={4}
                    value={quoteAddresses.shippingAddress.deliveryInstructions}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, 500);
                      setQuoteAddresses(prev => ({
                        ...prev,
                        shippingAddress: { ...prev.shippingAddress, deliveryInstructions: sanitized }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Special delivery instructions, timing preferences, etc."
                    maxLength={500}
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(quoteAddresses.shippingAddress.deliveryInstructions, 500)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Terms & Conditions Tab */}
          {activeTab === 'terms' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Terms & Conditions
                </label>
                <div className="space-y-1">
                  <textarea
                    rows={8}
                    value={quoteDetails.terms}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.terms);
                      setQuoteDetails({ ...quoteDetails, terms: sanitized });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Enter terms and conditions..."
                    maxLength={FIELD_LIMITS.terms}
                  />
                  <div className="text-right text-xs text-gray-500">
                    {getCharacterCount(quoteDetails.terms, FIELD_LIMITS.terms)}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <div className="space-y-1">
                  <textarea
                    rows={4}
                    value={quoteDetails.notes}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                      setQuoteDetails({ ...quoteDetails, notes: sanitized });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Enter additional notes..."
                    maxLength={FIELD_LIMITS.notes}
                  />
                  <div className="text-right text-xs text-gray-500">
                    {getCharacterCount(quoteDetails.notes, FIELD_LIMITS.notes)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div
              className={`space-y-6 p-4 rounded-lg transition-colors ${isDragging ? 'bg-green-50 border-2 border-dashed border-agrosaf-green' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Attachments</h3>
                <PermissionGate permission="quotation_management" action="update">
                  <Button
                    variant="primary"
                    onClick={() => document.getElementById('fileInput').click()}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Attachment</span>
                  </Button>
                </PermissionGate>
              </div>

              <input
                id="fileInput"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />

              {uploadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center text-red-800">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <span className="font-medium">Error:</span>
                  </div>
                  <p className="text-red-700 mt-1 ml-7">{uploadError}</p>
                </div>
              )}

              {isUploadingAttachment && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-800 font-medium">Uploading attachment... {uploadProgress}%</span>
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {attachments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {attachments.map((attachment, index) => (
                    <div key={attachment.id || index} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between bg-white">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 break-words" title={attachment.name || attachment.filename}>
                            {attachment.name || attachment.filename}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(attachment.size / 1024).toFixed(1)} KB
                          </p>
                          {attachment.isExisting && (
                            <span className="text-xs text-blue-600 mt-1 inline-block">Existing file</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                        {attachment.url && (
                          <button
                            type="button"
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="text-gray-400 hover:text-blue-500"
                            aria-label="Download attachment"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => removeAttachment(index)}
                          className="text-gray-400 hover:text-red-500"
                          aria-label="Remove attachment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 bg-white ${isDragging
                    ? 'border-agrosaf-green bg-green-50 scale-[1.02]'
                    : 'border-gray-300 hover:border-agrosaf-green hover:bg-gray-50'
                    }`}
                >
                  <FileText className={`mx-auto h-12 w-12 transition-colors ${isDragging ? 'text-agrosaf-green' : 'text-gray-400'}`} />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {isDragging ? 'Drop files here' : 'No attachments'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {isDragging ? 'Release to upload' : 'Only pdf and image files are allowed (Max 25Mb)'}
                  </p>
                  <div className="mt-6">
                    <PermissionGate permission="quotation_management" action="update">
                      <Button
                        variant="primary"
                        onClick={() => document.getElementById('fileInput').click()}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Upload File
                      </Button>
                    </PermissionGate>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Centered Create/Update Quote Button */}
      <div className="flex justify-center mt-6">
        <PermissionGate permission="quotation_management" action={currentQuoteId || quotationIdFromQuery ? "update" : "create"}>
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            onClick={handleCreateQuote}
            className={`${!isAllRequiredFieldsFilled()
              ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed opacity-60'
              : 'bg-agrosaf-green hover:bg-agrosaf-darkGreen'
              }`}
            disabled={
              (saveDraftMutation.isPending || createUpdateQuotationMutation.isPending || sending || isUploadingAttachment) ||
              // Disable if all required fields are not filled
              !isAllRequiredFieldsFilled()
            }
            title={
              !isAllRequiredFieldsFilled()
                ? 'Please fill all required fields: Quote Title, Expiry Date, Billing Address, Shipping Address, and at least one Product'
                : ''
            }
          >
            {(() => {
              const isUpdate = !!(currentQuoteId || quotationIdFromQuery);
              if (createUpdateQuotationMutation.isPending) {
                return isUpdate ? 'Updating...' : 'Creating...';
              }
              return isUpdate ? 'Update Quote' : 'Create Quote';
            })()}
          </Button>
        </PermissionGate>
      </div>

      {/* Add Product Modal */}
      {
        showAddProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className={`bg-white rounded-lg p-4 w-full mx-4 max-h-[90vh] overflow-y-auto ${quoteCustomerId ? 'max-w-6xl' : 'max-w-2xl'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Product</h3>
                <button
                  onClick={handleCancelAddProduct}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className={quoteCustomerId ? 'flex flex-col lg:flex-row gap-4' : ''}>
                {quoteCustomerId && (
                  <div className="lg:w-[480px] lg:shrink-0 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Previous purchases</h4>
                    {!newProductForm.productName ? (
                      <p className="text-xs text-gray-500">Select a product to see previous invoices.</p>
                    ) : loadingProductPurchaseHistory ? (
                      <p className="text-xs text-gray-500">Loading previous purchases...</p>
                    ) : productPurchaseHistory.length === 0 ? (
                      <p className="text-xs text-gray-500">No previous purchases for this product.</p>
                    ) : (
                      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-white sticky top-0">
                            <tr className="text-left text-gray-600 border-b border-gray-200">
                              <th className="py-2 pr-2 font-medium">Invoice number</th>
                              <th className="py-2 pr-2 font-medium">Product name</th>
                              <th className="py-2 pr-2 font-medium">Quantity</th>
                              <th className="py-2 font-medium">Order date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productPurchaseHistory.map((row, index) => (
                              <tr
                                key={`${row.invoiceNumber || 'inv'}-${row.invoiceDate || index}-${index}`}
                                className="border-b border-gray-100 last:border-b-0"
                              >
                                <td className="py-2 pr-2 text-gray-900 whitespace-nowrap">{row.invoiceNumber || '-'}</td>
                                <td className="py-2 pr-2 text-gray-900 capitalize break-words">{row.productName || '-'}</td>
                                <td className="py-2 pr-2 text-gray-900 whitespace-nowrap">{row.quantity ?? '-'}</td>
                                <td className="py-2 text-gray-900 whitespace-nowrap">{formatDate(row.invoiceDate, { includeTime: false }) || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

              <div className={quoteCustomerId ? 'flex-1 min-w-0 space-y-4' : 'space-y-4'}>
                {/* Product Selection - Searchable Dropdown */}
                <div className="relative product-dropdown-container">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Product <span className="text-red-500">*</span>
                    {process.env.NODE_ENV === 'development' && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({productCatalog.length} products loaded)
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        ref={productSearchInputRef}
                        type="text"
                        value={productSearch}
                        onChange={(e) => {
                          const sanitizedValue = sanitizeProductSearch(e.target.value);
                          setProductSearch(sanitizedValue);
                          setIsProductDropdownOpen(true);
                          setSelectedProductIndex(-1); // Reset selection when typing

                          // Clear selected product if previously selected
                          if (newProductForm.productId) {
                            setNewProductForm(prev => ({ ...prev, productId: '' }));
                          }

                          // Clear duplicate product error when user starts typing
                          if (productFormErrors.productId) {
                            setProductFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.productId;
                              return newErrors;
                            });
                          }
                        }}
                        onPaste={(e) => {
                          // Prevent pasting restricted symbols
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          const sanitizedValue = sanitizeProductSearch(pastedText);
                          setProductSearch(sanitizedValue);
                          setIsProductDropdownOpen(true);
                          setSelectedProductIndex(-1);

                          // Clear selected product if previously selected
                          if (newProductForm.productId) {
                            setNewProductForm(prev => ({ ...prev, productId: '' }));
                          }

                          // Clear duplicate product error when user pastes text
                          if (productFormErrors.productId) {
                            setProductFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.productId;
                              return newErrors;
                            });
                          }
                        }}
                        onFocus={() => {
                          wasInputFocusedRef.current = true;
                          if (productCatalog.length > 0) {
                            setIsProductDropdownOpen(true);
                          }
                        }}
                        onBlur={(e) => {
                          // Only mark as unfocused if focus is not moving to dropdown
                          // Use setTimeout to check where focus moved to
                          setTimeout(() => {
                            const activeElement = document.activeElement;
                            const isFocusInDropdown = activeElement?.closest('.product-dropdown-container');
                            if (!isFocusInDropdown) {
                              wasInputFocusedRef.current = false;
                            }
                          }, 100);
                        }}
                        onKeyDown={handleProductSearchKeyDown}
                        placeholder={loadingProducts ? 'Loading products...' : 'Search products...'}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${productFormErrors.productId ? 'border-red-300' : 'border-gray-300'
                          }`}
                      />
                    </div>
                    {isProductDropdownOpen && (
                      <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loadingProducts ? (
                          <div className="px-4 py-3 text-sm text-gray-500">Loading products...</div>
                        ) : productCatalog.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            {productSearch ? 'No products found' : 'Start typing to search products'}
                          </div>
                        ) : (
                          <>
                            {productCatalog.map((product, index) => {
                              const productName = product.name || product.productName || `Product ${index + 1}`;
                              const productCode = product.productCode || product.sku || '';
                              const displayText = productCode ? `${productName} (${productCode})` : productName;
                              const productKey = product.id || product._id || product.productId || `product-${index}`;
                              const isSelected = selectedProductIndex === index;
                              const isExpiredProduct = (() => {
                                if (!product.expiry) return false;
                                const expiryDate = new Date(product.expiry);
                                if (Number.isNaN(expiryDate.getTime())) return false;
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                expiryDate.setHours(0, 0, 0, 0);
                                return expiryDate < today;
                              })();

                              return (
                                <button
                                  key={productKey}
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  onMouseEnter={() => setSelectedProductIndex(index)}
                                  className={`w-full text-left px-4 py-2 focus:outline-none transition-colors border-b border-gray-100 last:border-b-0 ${isSelected
                                    ? 'bg-agrosaf-green bg-opacity-10 hover:bg-agrosaf-green hover:bg-opacity-20'
                                    : 'hover:bg-gray-100'
                                    }`}
                                  ref={(el) => {
                                    // Auto-scroll to selected item
                                    if (isSelected && el) {
                                      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                    }
                                  }}
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                      <div className={`font-medium capitalize break-words ${isExpiredProduct ? 'text-red-700' : 'text-gray-900'}`}>{productName}</div>
                                      {productCode && (
                                        <div className={`text-xs uppercase break-words ${isExpiredProduct ? 'text-red-600' : 'text-gray-500'}`}>{productCode}</div>
                                      )}
                                    </div>
                                    <div className={`flex flex-shrink-0 items-start gap-4 text-xs whitespace-nowrap ${isExpiredProduct ? 'text-red-600' : 'text-gray-500'}`}>
                                      <div>
                                        <div className={`font-medium ${isExpiredProduct ? 'text-red-700' : 'text-gray-700'}`}>MRP</div>
                                        <div>₹{(product.mrp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                      <div>
                                        <div className={`font-medium ${isExpiredProduct ? 'text-red-700' : 'text-gray-700'}`}>Unit Price</div>
                                        <div>₹{(product.price || product.rateA || product.rateB || product.rateC || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                      <div>
                                        <div className={`font-medium ${isExpiredProduct ? 'text-red-700' : 'text-gray-700'}`}>Expiry</div>
                                        <div className={isExpiredProduct ? 'text-red-600' : 'text-gray-500'}>{product.expiry ? formatDateDDMMYYYY(product.expiry) : '-'}</div>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {productFormErrors.productId && (
                    <p className="text-xs text-red-600 mt-1">{productFormErrors.productId}</p>
                  )}
                </div>

                {/* Product Details - Separate Rows */}
                <div className="space-y-4">
                  {/* Product Details Section - First Row */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 border-b pb-2">Product Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Product Name - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Product Name
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 capitalize">
                          {newProductForm.productName || '-'}
                        </div>
                      </div>

                      {/* Product ID - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Product ID
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 uppercase">
                          {newProductForm.sku || '-'}
                        </div>
                      </div>

                      {/* Actual Rate (Cost) - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Actual Rate
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          ₹{newProductForm.cost ? newProductForm.cost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </div>
                      </div>

                      {/* Purchase Rate - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Purchase Rate
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          ₹{newProductForm.purchaseRate ? newProductForm.purchaseRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </div>
                      </div>

                      {/* MRP - Read-only (show mrp if available, else rateA, rateB, rateC) */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          MRP
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          ₹{newProductForm.mrp ? newProductForm.mrp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </div>
                      </div>

                      {/* Unit Price - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Unit Price
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          ₹{newProductForm.unitPrice ? newProductForm.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </div>
                      </div>

                      {/* Batch - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Batch
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          {newProductForm.batch || '-'}
                        </div>
                      </div>

                      {/* Expiry Date - Read-only */}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Expiry Date
                        </label>
                        <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                          {formatDateDDMMYYYY(newProductForm.expiry)}
                        </div>
                      </div>

                      {/* Manufacturer - Read-only (if available) */}
                      {newProductForm.manufacturing && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Manufacturer
                          </label>
                          <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 capitalize">
                            {newProductForm.manufacturing}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing & Quantity Section - Second Row */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 border-b pb-2">Pricing & Quantity</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Quantity - Input */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={newProductForm.quantity}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow empty input for better UX
                            if (inputValue === '' || inputValue === null || inputValue === undefined) {
                              handleProductFormChange('quantity', '');
                              return;
                            }

                            // Parse the input value - handle both string and number
                            let numValue;
                            if (typeof inputValue === 'string') {
                              // Remove any non-numeric characters except decimal point
                              const cleaned = inputValue.replace(/[^\d.]/g, '');
                              if (cleaned === '' || cleaned === '.') {
                                handleProductFormChange('quantity', '');
                                return;
                              }
                              numValue = parseFloat(cleaned);
                            } else {
                              numValue = parseFloat(inputValue);
                            }

                            // If not a valid number, don't update
                            if (isNaN(numValue) || numValue < 0) {
                              return;
                            }

                            // Cap at available stock if stock > 0, otherwise cap at max limit
                            const stock = newProductForm.stock || 0;
                            if (stock > 0) {
                              // If stock is available, cap at stock limit
                              const maxQuantity = Math.min(stock, NUMBER_LIMITS.quantity.max);
                              if (numValue > maxQuantity) {
                                numValue = maxQuantity;
                              }
                            } else {
                              // If no stock limit, cap at max limit
                              if (numValue > NUMBER_LIMITS.quantity.max) {
                                numValue = NUMBER_LIMITS.quantity.max;
                              }
                            }

                            // Ensure minimum quantity
                            if (numValue < NUMBER_LIMITS.quantity.min) {
                              numValue = NUMBER_LIMITS.quantity.min;
                            }

                            // Update with capped value
                            handleProductFormChange('quantity', numValue);
                          }}
                          onBlur={(e) => {
                            // Ensure value is valid on blur
                            const value = parseFloat(e.target.value);
                            if (isNaN(value) || value < NUMBER_LIMITS.quantity.min) {
                              handleProductFormChange('quantity', NUMBER_LIMITS.quantity.min);
                            } else {
                              // Cap at available stock if stock > 0, otherwise cap at max limit
                              const stock = newProductForm.stock || 0;
                              const maxQuantity = stock > 0 ? Math.min(stock, NUMBER_LIMITS.quantity.max) : NUMBER_LIMITS.quantity.max;
                              const cappedValue = Math.min(value, maxQuantity);
                              if (cappedValue !== value) {
                                handleProductFormChange('quantity', cappedValue);
                              }
                            }
                          }}
                          onKeyDown={handleModalInputKeyDown}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${productFormErrors.quantity ? 'border-red-300' : 'border-gray-300'
                            }`}
                          min={NUMBER_LIMITS.quantity.min}
                          max={newProductForm.stock > 0 ? Math.min(newProductForm.stock, NUMBER_LIMITS.quantity.max) : NUMBER_LIMITS.quantity.max}
                          required
                        />
                        {productFormErrors.quantity && (
                          <p className="text-xs text-red-600 mt-0.5">{productFormErrors.quantity}</p>
                        )}
                        {newProductForm.stock > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">Available stock: {newProductForm.stock}</p>
                        )}
                      </div>

                      {/* Discount - Input */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Discount %
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={newProductForm.discount}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow empty input
                            if (inputValue === '') {
                              handleProductFormChange('discount', '');
                              return;
                            }

                            // Check if input has more than 2 decimal places
                            const decimalParts = inputValue.split('.');
                            if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                              // Round to 2 decimal places
                              const numValue = parseFloat(inputValue);
                              if (isNaN(numValue)) return;
                              const roundedValue = Math.round(numValue * 100) / 100;
                              const cappedValue = Math.max(NUMBER_LIMITS.discount.min, Math.min(roundedValue, NUMBER_LIMITS.discount.max));
                              handleProductFormChange('discount', cappedValue);
                              return;
                            }

                            const numValue = parseFloat(inputValue);
                            if (isNaN(numValue)) return;

                            // Cap at min/max limits
                            const cappedValue = Math.max(NUMBER_LIMITS.discount.min, Math.min(numValue, NUMBER_LIMITS.discount.max));
                            handleProductFormChange('discount', cappedValue);
                          }}
                          onBlur={(e) => {
                            // Round to 2 decimal places on blur
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              const roundedValue = Math.round(value * 100) / 100;
                              const cappedValue = Math.max(NUMBER_LIMITS.discount.min, Math.min(roundedValue, NUMBER_LIMITS.discount.max));
                              if (roundedValue !== value || cappedValue !== value) {
                                handleProductFormChange('discount', cappedValue);
                              }
                            }
                          }}
                          onKeyDown={handleModalInputKeyDown}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${productFormErrors.discount ? 'border-red-300' : 'border-gray-300'
                            }`}
                          min={NUMBER_LIMITS.discount.min}
                          max={NUMBER_LIMITS.discount.max}
                          placeholder="0"
                        />
                        {productFormErrors.discount && (
                          <p className="text-xs text-red-600 mt-0.5">{productFormErrors.discount}</p>
                        )}
                      </div>

                      {/* Tax - Input */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tax %
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={newProductForm.tax}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow empty input
                            if (inputValue === '') {
                              handleProductFormChange('tax', '');
                              return;
                            }

                            // Check if input has more than 2 decimal places
                            const decimalParts = inputValue.split('.');
                            if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                              // Round to 2 decimal places
                              const numValue = parseFloat(inputValue);
                              if (isNaN(numValue)) return;
                              const roundedValue = Math.round(numValue * 100) / 100;
                              const cappedValue = Math.max(NUMBER_LIMITS.tax.min, Math.min(roundedValue, NUMBER_LIMITS.tax.max));
                              handleProductFormChange('tax', cappedValue);
                              return;
                            }

                            const numValue = parseFloat(inputValue);
                            if (isNaN(numValue)) return;

                            // Cap at min/max limits
                            const cappedValue = Math.max(NUMBER_LIMITS.tax.min, Math.min(numValue, NUMBER_LIMITS.tax.max));
                            handleProductFormChange('tax', cappedValue);
                          }}
                          onBlur={(e) => {
                            // Round to 2 decimal places on blur
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              const roundedValue = Math.round(value * 100) / 100;
                              const cappedValue = Math.max(NUMBER_LIMITS.tax.min, Math.min(roundedValue, NUMBER_LIMITS.tax.max));
                              if (roundedValue !== value || cappedValue !== value) {
                                handleProductFormChange('tax', cappedValue);
                              }
                            }
                          }}
                          onKeyDown={handleModalInputKeyDown}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${productFormErrors.tax ? 'border-red-300' : 'border-gray-300'
                            }`}
                          min={NUMBER_LIMITS.tax.min}
                          max={NUMBER_LIMITS.tax.max}
                          placeholder="0"
                        />
                        {productFormErrors.tax && (
                          <p className="text-xs text-red-600 mt-0.5">{productFormErrors.tax}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grand Total Preview - Detailed Calculation */}
                {newProductForm.productId && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Line Calculation</h4>
                    <div className="space-y-2">
                      {/* Subtotal (Qty * Unit Price) */}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal ({newProductForm.quantity} × ₹{newProductForm.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}):</span>
                        <span className="font-medium text-gray-900">
                          ₹{(newProductForm.quantity * newProductForm.unitPrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>

                      {/* Discount Tracking */}
                      {newProductForm.discount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Discount ({newProductForm.discount}%):</span>
                          <span className="font-medium text-red-600">
                            - ₹{((newProductForm.quantity * newProductForm.unitPrice * newProductForm.discount) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}

                      {/* Tax Tracking */}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax ({newProductForm.tax}%):</span>
                        <span className="font-medium text-gray-900">
                          + ₹{(() => {
                            const subtotal = newProductForm.quantity * newProductForm.unitPrice;
                            const discount = (subtotal * (newProductForm.discount || 0)) / 100;
                            const taxable = subtotal - discount;
                            return ((taxable * (newProductForm.tax || 0)) / 100);
                          })().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-2 pt-2 flex justify-between items-center">
                        <span className="text-sm font-bold text-gray-700">Grand Total:</span>
                        <span className="text-lg font-bold text-agrosaf-green">
                          ₹{(() => {
                            const subtotal = newProductForm.quantity * newProductForm.unitPrice;
                            const discount = (subtotal * (newProductForm.discount || 0)) / 100;
                            const taxable = subtotal - discount;
                            const tax = (taxable * (newProductForm.tax || 0)) / 100;
                            return (taxable + tax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200">
                <Button
                  variant="primary"
                  icon={quoteItems.some(item => item.id === newProductForm.id) ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  onClick={handleConfirmAddProduct}
                  disabled={!newProductForm.productId}
                  className={`${!newProductForm.productId
                    ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-agrosaf-green hover:bg-agrosaf-darkGreen'
                    }`}
                >
                  {quoteItems.some(item => item.id === newProductForm.id) ? 'Update Product' : 'Add Product'}
                </Button>
              </div>
              </div>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

export default QuoteBuilder;

